<template>
  <div class="chat-container-panel" :class="{ 'no-menu': !state.store.querent_id, 'cr-menu-surface': menuSurface, 'cr-menu-still': menuCardsShown, 'cr-lesson-stage-panel': lessonStage }">
    <div class="chat-box-container">
      <!-- Care Package: who this reading is for. A row above the scroller (not
         inside it): it neither scrolls away nor covers the first line of chat.
         At the start of the interview only; gone after the first exchange.
           "for someone" opens one box: friends surface as you type, and no
           match means what was typed is the name. Never says whether the
           name belongs to a member. -->
      <div v-if="intentToggleOn" class="cr-intent" data-testid="care-intent">
        <div class="cr-intent-toggle" role="radiogroup" aria-label="This reading is for">
          <button type="button" role="radio" :class="{ on: !intentForSomeone }" :aria-checked="!intentForSomeone"
                  @click="intentSetMe" title="For me"><i aria-hidden="true">◉</i><span>for me</span></button>
          <!-- Once a name is committed the option IS the name: "♡ for Lark ✎".
               Tapping it reopens the box. -->
          <button type="button" role="radio" :class="{ on: intentForSomeone }" :aria-checked="intentForSomeone"
                  @click="intentSetSomeone" :title="intentSent ? `For ${intentSent}, tap to change` : 'For someone'">
            <i aria-hidden="true">♡</i><span :class="{ named: intentSent }">{{ intentSent ? `for ${intentSent}` : 'for someone' }}</span>
            <i v-if="intentSent" class="fas fa-pencil cr-intent-pencil" aria-hidden="true"></i>
          </button>
        </div>
        <div v-if="intentForSomeone && intentEditing" class="cr-intent-who">
          <label class="cr-intent-box">
            <small>For</small>
            <input ref="intentInput" v-model="intentName" type="text" autocomplete="off" spellcheck="false"
                   placeholder="their name" aria-label="Their name" data-testid="care-intent-name"
                   @input="intentPicked = ''; intentListOpen = true" @keydown.enter.prevent="intentCommit" @blur="intentCommit" />
            <!-- The way to be done: a labelled pill, live once there is a name. Enter does the same. -->
            <button type="button" class="cr-intent-done" :disabled="!intentName.trim()"
                    title="Done" data-testid="care-intent-done" @mousedown.prevent @click="intentCommit"><i class="fas fa-check" aria-hidden="true"></i><span>Done</span></button>
          </label>
          <ul v-if="intentListOpen && intentMatches.length" class="cr-intent-list" role="listbox">
            <li v-for="f in intentMatches" :key="f.querent_uuid || 'past:' + f.nick_name" role="option"
                @mousedown.prevent="intentPick(f)">
              <img v-if="f.avatar_url" :src="f.avatar_url" alt="" /><i v-else aria-hidden="true"></i>
              <span>{{ f.nick_name }}</span><small>{{ f.past ? 'sent to before' : 'in your circle' }}</small>
            </li>
          </ul>
        </div>
      </div>
      <!-- The session label on a phone (gift room walk #5): at 480px and
           under the header row that holds "In session with …" is hidden, so
           their own reading from the gift room keeps it as a row of its own
           above the conversation (never over its first line). -->
      <div v-if="phoneSessionLabel" class="cr-session-label" data-testid="session-label"><SessionStatus :text="status.text" :busy="status.busy" /></div>
      <!-- Chat Messages -->
      <div class="chat-container"
           :class="{ 'cr-empty': !messages.length, 'cr-menu': menuSurface, 'cr-menu-still': menuCardsShown,
                     'cr-grounding': inGroundingPause || groundingArrivingNow,
                     'cr-headed': statusShown,
                     'cr-lesson-stage': lessonStage }"
           ref="chat_container_ref">
        <!-- A slow breath of light behind the beat. Decoration only: it exists
             to pull the eye to the centre and to give the ten held seconds
             something to happen in. -->
        <div v-if="inGroundingPause" class="cr-grounding-glow" aria-hidden="true"></div>
        <template v-for="(message, index) in messages" :key="index">
          <!-- Stage 3 (recipient journey Part 6): her birthday, her sign, You &
               the sender, "kept here" (the Mirror sheet). The room puts it in
               the conversation where it starts, so later lines follow it. -->
          <StageThree
            v-if="message._context === 'stage-three' && message.stage_three"
            :class="{ 'cr-faded': message._faded }"
            :inv="message.stage_three.inv"
            :from-name="message.stage_three.from_name || ''"
            :name="message.stage_three.name || ''"
            :replay="!!message.stage_three.replay"
            @settled="$emit('stage-three-settled')"
            @open-mirror="$emit('open-mirror')"
            @reported="r => $emit('stage-three-reported', r)"
            @dated="$emit('stage-three-dated')"
          />
          <!-- Stage 4 (recipient journey Part 7): the people she named, as
               links in the Mystic's closing turn, and "Someone else". -->
          <StageFour
            v-if="message._context === 'stage-four' && message.stage_four"
            :class="{ 'cr-faded': message._faded }"
            :turn="message.stage_four"
            @pick="p => $emit('stage-four-pick', p)"
            @someone-else="onSomeoneElse"
          />
          <!-- A pulled card (Talk it through, or her card in a gift room;
               2026-10-21 talk card design): one cream panel, the question,
               the card, its phrase and the Mystic's reading, filled in place. -->
          <TalkCard
            v-if="message._context === 'talk-card' && message.talk_card"
            :class="{ 'cr-faded': message._faded }"
            :label="talkPanelLabel(message.talk_card)"
            :question-label="talkQuestionLabel(message.talk_card)"
            :card-id="message.talk_card.id"
            :question="message.talk_card.question"
            :card="message.talk_card.card"
            :image="message.talk_card.image"
            :reversed="!!message.talk_card.reversed"
            :line="message.talk_card.line"
            :reading="message.talk_card.reading"
            :reader-name="talkReaderName"
            :waiting="!!message._live && !message.talk_card.reading"
            :turn="!!message._live"
            :centered="!!message.talk_card.lesson_draw"
            :held="!!message._live && !!message.talk_card.lesson_draw && !!state.store.lesson_card_settling"
            :write-in="!!message._live && !!message.talk_card.lesson_draw"
            @written="onTalkCardWritten(message.talk_card)"
            @card-detail="openTalkCardDetail(message.talk_card)"
          />
          <!-- The gift room's write-back, sent (DESIGN-2 §39): their own
               message, right-aligned. It carries its own words, so it reads
               the same once the room has become their own reading. -->
          <GiftReply
            v-if="message._context === 'gift-reply-sent' && message.gift_reply"
            inline
            :class="{ 'cr-faded': message._faded }"
            :gift="message.gift_reply.gift || {}"
            :room="message.gift_reply.room || {}"
            :sealed="message.gift_reply.sealed || true"
          />
          <!-- "Add <sender> to your circle?" (2026-10-26): the Mystic's one
               ask after their reply; nothing is added without it. -->
          <CircleAsk
            v-if="message._context === 'circle-ask' && message.circle_ask"
            :class="{ 'cr-faded': message._faded }"
            :inv="message.circle_ask.inv"
            :ask="message.circle_ask"
          />
          <!--
            Suppress the bubble wrapper entirely when a message has no
            renderable content (no text, no imageUrl). This handles the
            transient case where tarot-session-image pushes a bubble with
            text:'' expecting a follow-up card-selected-in-chat-window
            frame to fill it in — if that frame is late, missing, or out
            of order, the empty wrapper used to render as a blank bubble.
          -->
          <!-- The grounding beat is large and centred while it is the only
               thing the Mystic has said; once the question lands under it, it
               settles into an ordinary line of the conversation. -->
          <div
            v-if="(message.text || message.imageUrl) && !message._holdForReading && !message._holdForSummary
                  && !(message.type === 'user' && isCommandEcho(message.text))"
            :class="['message-box', { 'cr-faded': message._faded }, openerClass(message),
                      message.type === 'user' ? 'user-message' : 'reader-response',
                      message.is_card === 'true' ? 'chat-card-selection' : '',
                      groundingBeatClass(message, index),
                      { 'cr-talk-recap': index === talkRecapIndex }]"
            :data-testid="index === talkRecapIndex ? 'talk-card-recap' : null"
          >
            <!-- Card Selected Image Message -->
            <template v-if="message.imageUrl">
              <div class="card-message">
                <!-- Card Title -->
                <div class="card-title">{{ message.card }}</div>
                <!-- Image and Text Content -->
                <div class="card-content">
                  <img
                    :src="message.imageUrl"
                    alt="Card Image"
                    class="card-image"
                    :class="{ 'reversed-card': message.reversed }"
                    @load="scrollChatBox"
                  />
                  <div class="card-text-container card-caption" v-if="message.text">
                      {{ message.text }}
                  </div>
                  <!-- The headline is on its way (2026-10-23): a quiet line in
                       its slot, so the panel never sits half-empty. -->
                  <div v-else-if="message._awaitingWords" class="card-text-container reveal-reading"
                       role="status" data-testid="reveal-reading">
                    <span class="reveal-reading-line">{{ revealReadingLine(message) }}</span>
                    <span class="reveal-dots" aria-hidden="true"><i></i><i></i><i></i></span>
                  </div>
                </div>
              </div>
            </template>
            <!-- Text Message -->
            <template v-else-if="message.text">
              <!-- The paths the question offers are links in the sentence itself. -->
              <!-- "I've kept these for you." (R5.6): "these" opens the Journal sheet. -->
              <p v-if="message.journal_link" class="text-message" data-testid="kept-line">
                <template v-for="(seg, si) in keptLineSegments(message.text)" :key="si">
                  <button v-if="seg.link" type="button" class="cr-choice-link" data-testid="kept-line-link"
                          @click="$emit('open-reading-history')">{{ seg.text }}</button>
                  <template v-else>{{ seg.text }}</template>
                </template>
              </p>
              <!-- The end of her introduction (R8.3): the link opens the Quest sheet. -->
              <p v-else-if="message.quest_link" class="text-message" data-testid="intro-line">
                <template v-for="(seg, si) in momentSegments(message.text, message.quest_link)" :key="si">
                  <button v-if="seg.link" type="button" class="cr-choice-link" data-testid="intro-line-link"
                          @click="$emit('open-quest')">{{ seg.text }}</button>
                  <template v-else>{{ seg.text }}</template>
                </template>
              </p>
              <!-- The Mystic's unlock line (navigation API §5): its links open Community and Quest. -->
              <p v-else-if="message.nav_links && message.nav_links.length" class="text-message" data-testid="nav-line">
                <template v-for="(seg, si) in navLineSegments(message.text, message.nav_links)" :key="si">
                  <button v-if="seg.to" type="button" class="cr-choice-link" :data-testid="`nav-line-${seg.to}`"
                          @click="$emit('nav-link', seg.to)">{{ seg.text }}</button>
                  <template v-else>{{ seg.text }}</template>
                </template>
              </p>
              <!-- Another voice (R9.4): the Mystic's name is the link to her page. -->
              <p v-else-if="message.reader_link" class="text-message" data-testid="recommend-line">
                <template v-for="(seg, si) in momentSegments(message.text, message.reader_link)" :key="si">
                  <button v-if="seg.link" type="button" class="cr-choice-link" data-testid="recommend-link"
                          @click="$emit('open-reader', message.reader_sub)">{{ seg.text }}</button>
                  <template v-else>{{ seg.text }}</template>
                </template>
              </p>
              <!-- Their own reading from the gift room opens on the bridge (redo,
                   2026-10-29): its fixed last line, "…or ask about anything else
                   on your mind.", sits under it, quiet. Ahead of the inline links: the bridge is
                   the Mystic's last line, and that branch took it whole (gift room walk #6). -->
              <template v-else-if="splitElseLine(message.text).quiet">
                <p class="text-message">{{ splitElseLine(message.text).text }}</p>
                <p class="text-message cr-else" data-testid="else-line">{{ splitElseLine(message.text).quiet }}</p>
              </template>
              <!-- Inline text is for replies (2026-10-27): what she might say
                   next is a link in the Mystic's line, never a chip. -->
              <!-- A link (not a button) so a long reply wraps with the sentence
                   instead of jumping to its own line. -->
              <p v-else-if="index === inlinedIndex" class="text-message">
                <template v-for="(seg, si) in inlined.segments" :key="si">
                  <a v-if="seg.action" href="#" role="button" class="cr-choice-link"
                     :data-testid="inlineTestId(seg.action)"
                     @click.prevent="onInlineChoice(seg.action)">{{ seg.text }}</a>
                  <br v-else-if="seg.joiner && seg.text === '\n'" />
                  <template v-else>{{ seg.text }}</template>
                </template>
              </p>
              <p v-else class="text-message">{{ message.text }}</p>
              <!-- Only the "find the words" I-response is foldable; gone once added. -->
              <div v-if="appendable && message.foldable && !message.appended" class="cr-fold">
                <button class="cr-append-btn" type="button"
                        @click="$emit('append-message', { text: message.text, selection: message.selection || '', message })">＋ Append to entry</button>
              </div>
              <!-- The assistant already saved. Say so, and offer the one move
                   that follows — on a phone the sheet covers the surface it
                   just changed, so without this it is a dead end. -->
              <div v-if="message.outcome" class="cr-outcome">
                <!-- The description itself, so it can be read before anything
                     is painted. It used to go straight into a builder textarea
                     the seeker could not see from here. -->
                <p v-if="message.outcome.description" class="cr-outcome-desc">
                  {{ message.outcome.description }}
                </p>
                <span class="cr-outcome-note">{{ message.outcome.confirm }}</span>
                <button class="cr-outcome-btn" type="button"
                        :disabled="message.outcome.busy"
                        @click="$emit('outcome-action', message)">
                  {{ message.outcome.busy ? 'Painting…' : message.outcome.cta + ' →' }}
                </button>
              </div>
            </template>
          </div>
          <!-- Name-picker option cards (same concept as the Studio) — full width
               below the assistant bubble. -->
          <!-- Each card states its own situation: yours, taken, how far short
               you are, or what it costs. Only one you can actually take is
               tappable — the picker used to look identical right up until the
               failure, and stayed tappable after the name was claimed. -->
          <div v-if="message.name_options && message.name_options.length && !message.claimed_slug"
               class="cr-name-options">
            <button
              v-for="opt in message.name_options.filter(o => !nameTiers || nameTiers.includes(o.word_count))"
              :key="opt.slug + ':' + opt.name" type="button" class="cr-name-card"
              :class="['state-' + nameOptionState(opt).state]"
              :disabled="nameOptionState(opt).disabled"
              @click="$emit('claim-name', opt)">
              <div class="cr-name-head">
                <span class="cr-name-name">{{ opt.name }}</span>
                <span class="cr-name-tier">{{ opt.word_count }} {{ opt.word_count === 1 ? 'word' : 'words' }}</span>
              </div>
              <div class="cr-name-meta">
                <span class="cr-name-slug">{{ nameSlugPrefix }}{{ opt.slug }}</span>
                <span class="cr-name-cost"
                      :class="{ free: nameOptionState(opt).cost === 0,
                                short: nameOptionState(opt).state === 'short' }">
                  {{ nameOptionState(opt).label }}
                </span>
              </div>
            </button>
            <button v-if="anyNameShort && !storeHidden" type="button" class="cr-name-topup"
                    @click="$emit('need-carats')">
              Get more carats
            </button>
          </div>
        </template>
        <!-- The grounding line is on its way: three gold dots where it will
             appear, until its first word lands; they fade as the line fades
             in (Chris, 2026-10-19). "Still with you…" if it is slow. -->
        <Transition name="gd-fade">
          <div v-if="groundingArrivingNow" class="grounding-arriving" data-testid="grounding-arriving">
            <BreathDots :still-after="4000" />
          </div>
        </Transition>
        <!-- Empty/preparing state: a themed loading screen so an un-started
             chat never shows as a bare white panel on the dark app. -->
        <div
          v-if="!messages.length && !groundingArrivingNow && !(showMenuCards && StaticSuggestions && StaticSuggestions.length)"
          class="cr-preparing"
        >
          <i class="fa-solid fa-spinner fa-spin cr-preparing-orb"></i>
          <p class="cr-preparing-text">Preparing your reading…</p>
          <!-- The socket is the only thing that clears this. If nothing has
               arrived after a while, say so and offer the way out; a wheel
               that never stops is what a tester reported (2026-09-23). -->
          <div v-if="preparingStalled" class="cr-stalled" role="status">
            <p>This is taking longer than usual.</p>
            <button type="button" class="cr-stalled-btn" data-testid="retry-welcome" @click="$emit('retry-welcome')">Try again</button>
          </div>
        </div>
        <!-- Show loading animation if waiting for response -->
        <div
          v-if="isFetchingResponse && messages.length && !groundingArrivingNow"
          class="message-box reader-response loading-container"
          :class="{ 'is-grounding-countdown': inGroundingPause }"
        >
          <!-- Grounding beat: a slow depleting ring instead of the generic
               "One moment…" dots, so the ~10s grounding pause reads as an
               intentional settling-in moment. -->
          <div v-if="inGroundingPause" class="grounding-countdown" aria-hidden="true">
            <svg class="gc-ring" viewBox="0 0 44 44">
              <circle class="gc-track" cx="22" cy="22" r="19" />
              <circle class="gc-progress" cx="22" cy="22" r="19" />
            </svg>
          </div>
          <template v-else>
            <BreathDots :label="thinkingMessage" />
            <span v-if="fetchStalled" class="cr-stalled-inline"> Still working. You can keep going below.</span>
          </template>
        </div>
        <!-- The reading's one rating (SP-R1): the last item of the reading, right
             above its end-of-reading buttons (Talk it through · Share). -->
        <!-- While she is talking it through, the conversation has the room;
             the rating comes back when the reply box closes. -->
        <!-- After a gift went out the end block below carries it (giftEnd). -->
        <!-- "What landed?" asked (her own reading from the gift room): the
             question and its box are the end of the chat until she answers;
             the rating and the bar under it read as its answer (bug walk #12). -->
        <ReadingEndRating v-if="endRating && readingActive && !talkComposer && !giftEnd && !reflectOpen" @rated="onEndRated" />
        <!-- Start-menu quick actions as large clickable cards (Doorman menu) -->
        <div v-if="menuCardsShown" class="qa-cards">
          <button
            v-for="(action, index) in StaticSuggestions"
            :key="index"
            class="qa-card"
            :class="actionVisual(action).cls"
            @click="selectQuickAction(action)"
          >
            <span class="qa-card__art" aria-hidden="true"><i :class="['fas', actionVisual(action).icon]"></i></span>
            <span class="qa-card__sheen" aria-hidden="true"></span>
            <span v-if="actionPrice(action.button_label) && !isSubscribed" class="qa-card__price">{{ actionPrice(action.button_label) }} <i class="fas fa-gem"></i></span>
            <span class="qa-card__title">{{ actionTitle(action.button_label) }}</span>
          </button>
        </div>
        <!-- Static Suggestions as a Message Bubble (non-menu states).
             Suppressed while a reading is on screen so leaked community
             greeter buttons (Explore Readers / Enter the Ritual / …) don't
             intrude — the reading's own Finish / Share live in the header,
             and the conclusion menu appears after Finish. -->
        <div
          v-else-if="chipRowVisible"
          class="message-box reader-response static_suggestions"
          :class="{ 'deeper-flow': chipActions.some(a => a && a.conversation_type === 'DRAW_DEEPER'), 'gift-end': giftEnd,
                    'lesson-chips': lessonRow, 'ldp-chips-held': lessonRow && chipsHeld, 'ldp-chips-up': lessonRow && !chipsHeld,
                    'ldp-chips-leaving': lessonRow && lessonChipsLeaving }"
          :data-testid="giftEnd ? 'gift-end' : null"
        >
          <!-- A gift that went out (DESIGN 2026-10-22 §4): the reading's own
               rating, open while unrated, then Finish reading and Read the gift. -->
          <ReadingEndRating v-if="giftEndRating" @rated="onEndRated" />
          <!-- Chips are tools (2026-10-27): plain, small, one row under the
               conversation; a tap does something to the session and is never
               said as her words. -->
          <div class="static_suggestions tool-row" :class="{ compact: rowActions.length >= 3 && !lessonRow, 'gift-end-row': giftEnd, 'lesson-row': lessonRow }"
               :data-testid="lessonRow ? 'lesson-tool-row' : null">
            <button
              v-for="(action, index) in rowActions"
              v-show="isActionVisible(action)"
              :key="index"
              :data-testid="action._draw ? 'draw-the-cards' : (lessonRow && action.action_type === 'lesson_full_reading' ? 'lesson-go-deeper' : null)"
              :class="{ 'qa-reveal-cta': action.action_type === 'reveal_myself',
                        'qa-primary': isPostReadingPrimary(action) && !giftEnd,
                        'qa-primary-fill': rowHasFill && action._draw,
                        'qa-ghost': rowHasFill && !action._draw,
                        'qa-finish': giftEnd && action.action_type === 'exit_reading',
                        'qa-gift-open': giftEnd && action.action_type === 'share_reading',
                        'qa-landed': shareLanded && action.action_type === 'share_reading',
                        'qa-voice': action.conversation_type === 'DRAW_DEEPER' || isVoiceStarter(action) }"
              @click="selectQuickAction(action)"
            >
              <i v-if="action._draw" class="fas fa-layer-group qa-primary-glyph" aria-hidden="true"></i>
              <i v-else-if="action.action_type === 'reveal_myself'" class="fas fa-sparkles qa-reveal-glyph"></i>
              <i v-else-if="action.action_type === 'rate_reading'" class="fas fa-star qa-primary-glyph"></i>
              <i v-else-if="action.action_type === 'share_reading'" :class="['fas', 'qa-primary-glyph', (giftAction(state?.store?.reading_gift) || {}).icon || 'fa-share-nodes']"></i>
              <!-- After a lesson's card (2026-10-28): ✦ Go deeper with {Mystic} · 50 ✦;
                   the first ✦ is an ornament, the last the price's carat mark. -->
              <template v-if="lessonRow && action.action_type === 'lesson_full_reading'">
                <template v-for="(seg, si) in lessonChipSegments(action, { pricesVisible: !(state && state.store && state.store.prices_visible === false) })" :key="si">
                  <span v-if="seg.orn" class="lesson-orn" aria-hidden="true">✦</span>
                  <span v-else-if="seg.carat" class="lesson-carat" aria-label="carats">✦</span>
                  <span v-else class="lesson-chip-t">&nbsp;{{ seg.text }}&nbsp;</span>
                </template>
              </template>
              <template v-else>
                <template v-for="(seg, si) in renderActionLabel(actionLabel(action))" :key="si">
                  <!-- The lesson's chips name their price with the ✦ itself (E2.6). -->
                  <span v-if="seg.icon && lessonRow" class="lesson-carat" aria-label="carats">✦</span>
                  <CaratMark v-else-if="seg.icon" :size="13" /><span v-else>{{ seg.text }}</span>
                </template>
              </template>
            </button>
            <!-- Logged-out shared-reading viewer: Rate + Share are owned by the
                 logged-in seeker, so swap them for a single Login CTA. Signing in
                 (then ToS) is what attributes the rating/share + captures the
                 referral. -->
            <button
              v-if="!seekerLoggedIn && postReadingHasRateShare"
              class="qa-login-cta"
              type="button"
              @click="$emit('request-login')"
            >
              {{ loginCtaLabel }}
            </button>
          </div>
          <!-- Inline crystal-ball rating, revealed when the seeker taps Rate.
               Persists (no close) and is always re-tappable to change the rating.
               (Share opens the SharePopup modal, handled by ReaderPage.) -->
          <RatingCrystals v-if="rateOpen" />
        </div>

        <!-- A care package: the gift's own chips close the chat in place of
             the owner's Rate / Share. The write-back is the reply card at the
             letter's end (2026-10-22 §7); its chip scrolls there. -->
        <!-- "Tell your son what it meant" (DESIGN-2 §39): the reply card
             opens in place of the chips, where they tapped. -->
        <GiftReply
          v-if="giftReplyOpen && giftReply"
          ref="giftReplyRef"
          inline
          :gift="giftReply.gift || {}"
          :room="giftReply.room || {}"
          :short="giftReply.short || ''"
          :inv="giftReply.inv || ''"
          :before-send="giftReply.beforeSend || null"
          :on-sent="p => $emit('gift-reply-sent', p)"
          @dismiss="$emit('gift-reply-dismiss')"
        />
        <GiftChipBar
          v-else-if="giftToolChips.length"
          :chips="giftToolChips"
          @chip="key => $emit('gift-chip', key)"
        />
        <!-- "Keep this" (R5.9): quiet, never a gate, for a guest place only. -->
        <KeepThis v-if="keepOffer" />

        <!-- Calendar Widget -->
        <div
          v-if="currentWidget && currentWidget.type === 'birthdate'"
          class="message-box reader-response"
        >
          <BirthMoment
            mode="inline" :fields="['date', 'time']" :optional="['time']" submit-label="That's my birthday"
            @save="onChatBirthMoment"
          />
        </div>


      <!-- World Map Birthplace Widget -->
      <div
        v-if="currentWidget && currentWidget.type === 'birthplace_map'"
        class="message-box reader-response birthplace-map-wrapper"
      >
        <BirthMoment
          mode="inline" :fields="['place']" submit-label="That's where I was born"
          @save="onChatBirthplace"
        />
      </div>

<!-- Email Collection Widget -->
<div
  v-if="currentWidget && currentWidget.type === 'collect_email_form'"
  class="message-box reader-response"
>
  <CollectEmailWidget
    :payload="currentWidget.payload"
    @submit="email => submitEmailViaQuickAction(email)"
  />
</div>

<CountdownTimerWidget
  v-if="currentWidget && currentWidget.type === 'countdown_timer'"
  :payload="currentWidget.payload"
/>

<!-- Plugin Widget Fallback -->
<div
  v-if="currentWidget && pluginWidgetComponent"
  class="message-box reader-response"
>
  <component
    :is="pluginWidgetComponent"
    :payload="currentWidget.payload"
    @submit="value => submitPluginWidget(value)"
  />
</div>
      </div>

      <div class="reader-nav-container">
        <!--
          Doorman v1.2 — persistent ← Menu button.
          Renders when the seeker is in a Doorman-dispatched
          non-reading state and gives them a one-tap exit back to
          the Doorman menu. The trigger key 'back_to_doorman' is
          handled by Education / AstrologyRegistration /
          PersonalizedAstrology state classes (Phase A added these).
          Hidden during reading flow (SELECTING_CARDS,
          TAROT_N_CARD_DRAW, GAME_TAROT_4_CARDS) so we don't crowd
          the cards-on-table UX.
        -->
        <button
          v-if="showDoormanBackButton && !readingActive"
          class="reader-nav-button back-button"
          data-testid="doorman-back-button"
          @click="onBackToDoorman"
        >
          ← Menu
        </button>
        <!-- Exit Reading — the avatar-focus grounding / interview / funnel
             phase is the one surface with no other way out, so give the
             seeker a single one-tap button back to the reader's Doorman
             menu. ("Change Subject" already lives in the funnel's own
             centered button, so no dropdown is needed here.) -->
        <button
          v-if="showExitReading"
          class="reader-nav-button back-button exit-reading-trigger"
          type="button"
          data-testid="exit-reading-option"
          @click="onExitReading"
        >
          Exit Reading
        </button>
        <!-- Back Button -->
        <button
          class="reader-nav-button back-button"
          v-for="(action, index) in LeftActionButton"
          :key="index"
          @click="selectQuickAction(action)"
        >
          <template v-for="(seg, si) in renderActionLabel(toolChipLabel(action.button_label, action))" :key="si">
            <CaratMark v-if="seg.icon" :size="13" /><span v-else>{{ seg.text }}</span>
          </template>
        </button>
        <!-- The session status (2026-10-30): where the seeker is, in one line. -->
        <div class="reader-name-tab" v-if="statusShown"><SessionStatus :text="status.text" :busy="status.busy" /></div>

        <!-- Top-right control cluster: Finish Reading (once a reading is
             delivered) + Reading History link. Finish runs the same finish
             flow as the old chat pill; it just lives in the control panel. -->
        <div class="chat-top-controls">
          <button
            v-if="showTopFinish"
            class="finish-reading-control"
            type="button"
            @click="selectQuickAction(finishAction)"
          >
            {{ finishAction.button_label }}
          </button>
        </div>

        <!-- Forward Button -->
        <!-- Doorman v1: when the Interview state signals
             ready_for_reading=true, visually emphasize the action
             that begins the reading. The button is always rendered
             and always clickable regardless — this is purely a soft
             nudge, never a gate. -->
        <button
          class="reader-nav-button forward-button"
          :class="{ 'is-emphasized': isReadingActionEmphasized(action) }"
          v-for="(action, index) in navRightActions"
          :key="index"
          @click="selectQuickAction(action)"
        >
          <template v-for="(seg, si) in renderActionLabel(toolChipLabel(action.button_label, action))" :key="si">
            <CaratMark v-if="seg.icon" :size="13" /><span v-else>{{ seg.text }}</span>
          </template>
        </button>
      </div>

      <!-- Mode bar (Magic / Spoken) — sits above the input for scoped chats that
           shape a value (e.g. the affirmation). Magic = Aida shapes it (with a
           length); Spoken = keep the seeker's exact words (safety-checked). -->
      <div v-if="chatModes && chatModes.length" class="chat-mode-bar">
        <select class="chat-mode-select" :value="chatMode"
                :title="chatMode === 'magic' ? 'Aida shapes your words' : 'Keep your exact words (safety-checked)'"
                @change="$emit('update:chatMode', $event.target.value)">
          <option value="magic">✨ Magic</option>
          <option value="spoken">🪶 Spoken</option>
        </select>
        <label v-if="chatMode === 'magic'" class="chat-mode-length">
          Length
          <input type="number" :min="lengthRange[0]" :max="lengthRange[1]" :value="chatLength"
                 @input="$emit('update:chatLength', Number($event.target.value) || lengthRange[0])"
                 @blur="$emit('update:chatLength', clampLength($event.target.value))" />
          <span class="chat-mode-length-unit">words</span>
        </label>
      </div>

      <!-- Name tier filter (name-picker scope) — sit above the input like the
           Studio. Each chip toggles whether candidates of that word count show. -->
      <div v-if="nameScope" class="cr-tier-filter">
        <button v-for="t in [1, 2, 3, 4]" :key="t" type="button" class="cr-tier-chip"
                :class="{ on: !nameTiers || nameTiers.includes(t) }" @click="$emit('toggle-name-tier', t)">
          {{ t }} {{ t === 1 ? 'word' : 'words' }}
        </button>
      </div>

      <!-- Scoped suggestion chips (Altar/Keeper assistant) — sit just above the
           input. Tapping one sends it as a shaping message. -->
      <div v-if="bottomSuggestions && bottomSuggestions.length" class="bottom-suggestions">
        <button v-for="s in bottomSuggestions" :key="s" class="bottom-suggestion-chip"
                @click="$emit('suggestion', s)">{{ s }}</button>
      </div>

      <!-- Follow-up Container. Over a reading on screen it opens only for
           the conversation after it ("Talk it through"), or a gift's own
           deeper-card chat; see showComposer. -->
      <div class="follow-up-container" v-if="composerVisible">
        <!-- Actions menu trigger (gear) — collapses the reading actions
             (Exit / Change Subject / Select Cards) + insight help into a menu
             to the left of the input to save space. -->
        <!-- Drawn only when the menu has something the screen does not already offer. -->
        <div class="quick-actions-trigger reading-actions-menu" v-if="menuHasItems" ref="quickActionsTrigger">
          <!-- The gear stays while a reply is in flight: Exit / Change the
               Subject used to vanish behind a spinner, so a stuck reply also
               took the way out with it. The wait shows on the send button. -->
          <button @click="toggleQuickActions" aria-label="Reading options" title="Reading options">
            <i class="fa-solid fa-ellipsis"></i>
          </button>
        </div>
        <!-- Text Input and Send Button -->
        <textarea
            v-model="internal_follow_up_user_message"
            :placeholder="placeholderShown"
            rows="1"
            @keydown.enter.prevent="sendChat()"></textarea>
        <!-- Talk it through: the turns left, quiet, gold on the last one. -->
        <span
          v-if="talkCountdown"
          class="talk-left"
          :class="{ 'is-last': talkCountdown.last }"
          data-testid="talk-left"
          aria-live="polite"
        >{{ talkCountdown.text }}</span>
        <button @click="sendChat()" :class="{ 'is-waiting': isFetchingResponse && !fetchStalled }" aria-label="Send">
          <i v-if="isFetchingResponse && !fetchStalled" class="fa-solid fa-spinner fa-spin"></i>
          <i v-else class="fa-solid fa-paper-plane"></i>
        </button>
      <!-- Actions Dropdown: the reading actions (Exit / Change Subject / Select
           Cards) plus any insight-help quick actions, opened from the gear. -->
        <div
          class="quick-actions-dropdown"
          v-if="showQuickActions"
          ref="quickActionsDropdown"
        >
          <button v-if="finishAction" class="qa-reading-action" @click="selectQuickAction(finishAction)">
            <i class="fas fa-flag-checkered qa-action-icon"></i><span class="qa-action-label">{{ finishAction.button_label }}</span>
          </button>
          <!-- Talk it through: Share and the seeker's own card pull sit here
               while the conversation is open, not under every reply. -->
          <button v-if="talkShareAction" class="qa-reading-action" data-testid="talk-menu-share" @click="selectQuickAction(talkShareAction)">
            <i :class="['fas', 'qa-action-icon', (giftAction(state?.store?.reading_gift) || {}).icon || 'fa-share-nodes']"></i><span class="qa-action-label">{{ actionLabel(talkShareAction) }}</span>
          </button>
          <button v-if="talkPullMenuAction" class="qa-reading-action" data-testid="talk-menu-pull" @click="selectQuickAction(talkPullMenuAction)">
            <i class="fas fa-layer-group qa-action-icon"></i><span class="qa-action-label">{{ talkPullMenuAction.button_label }}</span>
          </button>
          <button v-if="showExitReading" class="qa-reading-action" @click="onExitReading">
            <i class="fas fa-arrow-right-from-bracket qa-action-icon"></i><span class="qa-action-label">Exit Reading</span>
          </button>
          <button
            v-for="(action, index) in menuList(LeftActionButton)"
            :key="'l' + index" class="qa-reading-action"
            @click="selectQuickAction(action)"
          >
            <i class="fas qa-action-icon" :class="actionMenuIcon(action)"></i>
            <span class="qa-action-label"><template v-for="(seg, si) in renderActionLabel(toolChipLabel(action.button_label, action))" :key="si"><CaratMark v-if="seg.icon" :size="13" /><span v-else>{{ seg.text }}</span></template></span>
          </button>
          <button
            v-for="(action, index) in menuList(RightActionButton)"
            :key="'r' + index" class="qa-reading-action"
            @click="selectQuickAction(action)"
          >
            <i class="fas qa-action-icon" :class="actionMenuIcon(action)"></i>
            <span class="qa-action-label"><template v-for="(seg, si) in renderActionLabel(toolChipLabel(action.button_label, action))" :key="si"><CaratMark v-if="seg.icon" :size="13" /><span v-else>{{ seg.text }}</span></template></span>
          </button>
          <button
            v-for="(action, index) in menuQuickActions"
            :key="'q' + index" class="qa-reading-action"
            @click="selectQuickAction(action)"
          >
            <i class="fas qa-action-icon" :class="actionMenuIcon(action)"></i>
            <span class="qa-action-label"><template v-for="(seg, si) in renderActionLabel(toolChipLabel(action.button_label, action))" :key="si"><CaratMark v-if="seg.icon" :size="13" /><span v-else>{{ seg.text }}</span></template></span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { showComposer } from '@/utils/chat-composer';
import CaratMark from '@/components/ui/CaratMark.vue';
import { routeQuickAction, isCommandEcho } from '@/utils/quick-action-route';
import { gutAskOpen, GUT_PLACEHOLDER, turnOpensPicker } from '@/utils/gut-line';
import { chatPinEdge, menuOverflow, revealTopShortfall, talkCardLead, leadScrollTop } from '@/utils/chat-pin-edge';
import { intentToggleVisible, intentSuggestions, giftAction } from '@/utils/care-package';
import { getMyFriends, careSent, saveBirthMoment } from '@/api-service';
import { drawCardsOffered, drawCardsReady, drawCardsChip, withoutSelectCards, readerSelectCards } from '@/utils/draw-cards-offer';
import { primaryFirst, toolChipLabel } from '@/utils/primary-chip';
import { inlineChoices, isReply } from '@/utils/inline-choices';
import { menuWithoutChips } from '@/utils/chat-menu';
// Lines the app adds to the chat itself, never a turn the paths belong to.
const APP_NOTES = new Set(['wrote-back', 'quest-line']);
import { talkFor, talkComposerOpen, countdown as talkCountdownOf, talkActionLabel, withoutOpenTalkChip, talkEnded, talkPullAllowed, talkPullAction, talkMenuExtras } from '@/utils/talk-it-through';
import { chooseInlineLink } from '@/utils/gift-opener';
import { optionState as nameOptionStateOf } from '@/utils/name-claim';
import {
  ReaderConversation,
  lock_interface,
  showLeftActionButton,
  showRightActionButton,
  LeftActionButton,
  RightActionButton,
  StaticSuggestions,
  ChatInstructions,
  disable_insight_help,
  currentWidget,
  loadingHeadsUpMessage,
  readingIsReady,
  getSunSign,
  formatPrettyDate,
} from "@/utils/reader-conversation";
import BirthMoment from '@/components/widgets/BirthMoment.vue'
import CollectEmailWidget from '@/components/widgets/CollectEmailWidget.vue';
import CountdownTimerWidget from '@/components/widgets/CountdownTimerWidget.vue';
import RatingCrystals from '@/components/ui/RatingCrystals.vue';
import ReadingEndRating from '@/components/ui/ReadingEndRating.vue';
import GiftChipBar from '@/components/ui/GiftChipBar.vue';
import GiftReply from '@/components/ui/GiftReply.vue';
import CircleAsk from '@/components/ui/CircleAsk.vue';
import TalkCard from '@/components/ui/TalkCard.vue';
import { isLessonToolRow, isLessonPrimary, lessonChipLabel, lessonChipSegments, lessonOneStep } from '@/utils/lesson-draw';
import { lessonChipsHeld, chipsLeaveMs, prefersReducedMotion } from '@/utils/lesson-presence';
import { panelLabel as talkPanelLabelOf, panelQuestionLabel } from '@/utils/talk-it-through';
import { openCardDetailFor } from '@/utils/card-detail';
import KeepThis from '@/components/ui/KeepThis.vue';
import StageThree from '@/components/ui/StageThree.vue';
import StageFour from '@/components/ui/StageFour.vue';
import BreathDots from '@/components/ui/BreathDots.vue';
import SessionStatus from '@/components/ui/SessionStatus.vue';
import { sessionStatus } from '@/utils/session-status';
import { groundingArriving } from '@/utils/grounding-arriving';
import { peopleReadingActions } from '@/utils/stage-four';
import { keptLineSegments } from '@/utils/stage-two';
import { splitElseLine } from '@/utils/gift-steps';
import { momentSegments } from '@/utils/introduction';
import { navLineSegments } from '@/utils/nav-stage';

import { adjustPanesForChat } from "@/utils/resize_pane";
import { store, static_store } from '@/store';
import { loginCtaLabelFor } from '@/utils/reading-lock';
import {reactive} from "vue";
import {pluginRegistry} from "@/plugins";
import { readingLine, positionOf } from '@/utils/reveal-pacing';
import { firstWordsDue, starterAction, liveReplies } from '@/utils/lesson-first-words';


const state = reactive({
  store,
  static_store,
});

export default {
  components: {
    CaratMark, BirthMoment, CollectEmailWidget, CountdownTimerWidget, RatingCrystals, ReadingEndRating, GiftChipBar, GiftReply, CircleAsk, TalkCard, KeepThis, StageThree, StageFour, BreathDots, SessionStatus },
  props: {
    state: Object,
    messages: Array,
    isFetchingResponse: Boolean,
    local_follow_up_user_message: String,
    quickActions: Array,
    // The rating just landed (3+): the bar's SHARE is glowed once.
    shareLanded: { type: Boolean, default: false },
    // True while a reading component is mounted — used to keep the Reading
    // History link out of the way during the reading (the post-delivery
    // conversation_state isn't always in the in-session list).
    readingActive: Boolean,
    // The reader's interview pace (quick / balanced / patient) and the seeker
    // messages before the cards are ready (the backend's
    // interview_answers_before_cards; null → the pace's fallback in
    // utils/draw-cards-offer) — when "Draw the cards" is ready.
    interviewPace: { type: String, default: 'balanced' },
    interviewAnswersBeforeCards: { type: Number, default: null },
    // Has the seeker actually reached the weave? Rate / Share are a response to
    // the whole reading, so they wait for it. Defaults true so every other host
    // of this chat (the Altar assistant, the Voice) is unaffected.
    summarySeen: { type: Boolean, default: true },
    // The reading's one rating is due here (the chat has reached the summary).
    endRating: { type: Boolean, default: false },
    // This instance owns its own conversation (the Altar assistant, the Voice)
    // rather than the reader's. It must not inherit the reader's chat lock or
    // its card-pick state — the Doorman locking the reader chat used to leave
    // the Altar assistant with a mode bar and nowhere to type.
    standalone: { type: Boolean, default: false },
    // Inline crystal-ball rating row (revealed by the post-reading Rate button).
    rateOpen: Boolean,
    // Tappable suggestion chips shown just above the input (Altar/Keeper helper).
    bottomSuggestions: { type: Array, default: () => [] },
    // Hide the Reading History link (e.g. on the Altar/Keeper assistant).
    suppressHistory: { type: Boolean, default: false },
    // Magic/Spoken mode bar above the input (for scoped value-shaping chats like
    // the affirmation). null = no mode bar. Magic shows a length input.
    chatModes: { type: Array, default: null },
    chatMode: { type: String, default: 'magic' },
    chatLength: { type: Number, default: 8 },
    lengthRange: { type: Array, default: () => [3, 30] },
    // Name-picker scope (same as the Studio): renders option cards in the stream
    // and a tier filter above the input. nameTiers = active word-count filter;
    // nameSlugPrefix prefixes the slug display (e.g. '/seeker/').
    nameScope: { type: Boolean, default: false },
    // R8.8: during her introduction the store is not reachable; no top-up link.
    storeHidden: { type: Boolean, default: false },
    nameTiers: { type: Array, default: null },
    // Balance + current handle, so a card can price itself honestly.
    carats: { type: Number, default: 0 },
    currentSlug: { type: String, default: null },
    nameSlugPrefix: { type: String, default: '' },
    // Journal scope: show "Add to entry" under each message.
    appendable: { type: Boolean, default: false },
    // "A reading for someone" (the entry beside Get Reading): when the intent
    // toggle appears at the start of the interview, open it on "for someone"
    // with the name box focused. Applied once; the parent clears it.
    intentPreset: { type: String, default: '' },
    // With the 'someone' preset: whom it is for ("Send Sam one back").
    intentPresetName: { type: String, default: '' },
    // The for-someone entry: the Mystic's first question waits for the name
    // (R3.3). "for me" from here still says so, so the question can be asked.
    openingHeld: { type: Boolean, default: false },
    // A reading for someone: no grounding ring or glow before its question.
    skipGrounding: { type: Boolean, default: false },
    // Care Package room: the gift's chips and the sender's name.
    giftChips: { type: Array, default: () => [] },
    giftSender: { type: String, default: '' },
    // Draw deeper is asking what is unclear: the composer stays up over the
    // sender's (finished) reading until she has answered.
    giftComposer: { type: Boolean, default: false },
    // Talk it through: the reply box was opened from the end-of-reading menu
    // (or here, then the chat remounted); the page owns it so it survives.
    talkOpen: { type: Boolean, default: false },
    // A card pulled mid-conversation is being drawn: the deck is the way
    // forward, so the reply box waits (the chat's "no actions" guard would
    // otherwise force it open under the picker).
    talkPicking: { type: Boolean, default: false },
    // A talk with no spread on screen (lesson-redo E2: after a lesson's card,
    // or "Talk it over with {Mystic} instead"): the state is TAROT_N_CARD_DRAW
    // but nothing is being picked, and the reply box, its count and the end
    // chips behave as over a reading (lesson walk B2, B5).
    talkSurface: { type: Boolean, default: false },
    // The card drawn from a lesson, on its own before "Go deeper" (2026-10-28):
    // no outline box, the card and its one step centred in the room.
    lessonStage: { type: Boolean, default: false },
    // The page's card picker is up (showCardDeck + its layer): the deck is
    // the way forward (the store never holds card_deck; lesson walk B4).
    deckShown: { type: Boolean, default: false },
    giftStarsOpen: { type: Boolean, default: false },
    giftInv: { type: String, default: '' },
    // The gift room's write-back, open in place of the chips (DESIGN-2 §39):
    // { gift, room, short, inv, beforeSend }.
    giftReply: { type: Object, default: null },
    giftReplyOpen: { type: Boolean, default: false },
    // Their own reading from the gift room (DESIGN-2 §40): "Ask Mystic Aida
    // anything…", and the box focused whenever the signal moves.
    composerPlaceholder: { type: String, default: '' },
    composerFocusSignal: { type: Number, default: 0 },
    // The Lessons tab's "Ask the cards about it" (community-simplify R3): the
    // visitor's own words wait in the box once it shows, ready to send.
    composerPrefill: { type: String, default: '' },
    // The words chosen under a lesson (lesson-ending §3): { words, how }, sent
    // as the seeker's first message once the Mystic's lesson opener is in.
    // A reply is the opener's own starter (the same tap); typed words are theirs.
    composerSend: { type: Object, default: null },
    intentHidden: { type: Boolean, default: false },
    // Stage 2: "Keep this" for a guest place (R5.9), and "What landed?" open
    // in the Respond box: her next line is kept on her reading (R5.4), not sent
    // to the Mystic.
    keepOffer: { type: Boolean, default: false },
    reflectOpen: { type: Boolean, default: false },
    // Stage 4: after "Someone else" her next line says who (R7.3); the page
    // reads it, the Mystic does not.
    someoneOpen: { type: Boolean, default: false },
    // What the page knows about the session for its status line
    // (utils/session-status): { pickCount, picked, readingCards, painting,
    // giftFor, quietPick }. null on every other host of this chat.
    sessionCues: { type: Object, default: null },
  },
  emits: ['composer-prefilled', 'composer-sent', 
    'retry-welcome',
    'suggestion', 'add-message', 'update:showLogin', 'updateQuickActions',
    'hideQuickActions', 'interfaceTrigger', 'updateFetchingResponse', 'exitReading',
    'exit-session', 'open-reading-history', 'open-quest', 'nav-link', 'typed-line', 'open-reader', 'request-login', 'open-mirror', 'stage-three-reported', 'stage-three-dated', 'stage-three-settled',
    'update:chatMode', 'update:chatLength', 'claim-name', 'toggle-name-tier',
    'append-message',
    'need-carats',
    'outcome-action',
    'care-intent',
    'intent-preset-applied',
    'gift-chip', 'open-composer', 'gift-stars-done', 'gift-reply-sent', 'gift-reply-dismiss',
    'reflect',
    'stage-four-pick', 'stage-four-someone', 'someone', 'reading-rated',
    'talk-open', 'talk-more',
  ],
  data() {
    return {
      // The lesson cards whose reading has written in (their chips may come up).
      writtenTalkCards: [],
      // Rated inside the gift's end block: the crystals stay with the answer.
      giftRatedHere: false,
      // The birth moment the chat's date step gathered, finished by the place step.
      chatBirthMoment: null,
      showQuickActions: false,
      // "Get the full reading": the chips fade before the turn goes (2026-10-28).
      lessonChipsLeaving: false,
      // Deadlines on the two socket-fed spinners (see the template).
      preparingStalled: false,
      preparingTimer: null,
      fetchStalled: false,
      fetchTimer: null,
      // Care Package intent (the toggle + the one box)
      intentForSomeone: false,
      intentName: '',
      intentPicked: '',      // querent_uuid when a friend was picked
      intentFriends: [],
      intentSent_: [],       // people I have sent a care package to before
      intentSent: '',        // the last name committed, so blur/enter don't repeat
      intentListOpen: false, // matches show while typing; a pick or a commit closes them
      intentEditing: false,  // the box is open (until Done); the chip shows the name after
      intentPresetDone: false, // the 'someone' preset is applied once per entry
      // Grounding greetings that have had their one beat (see markSpentGreetings).
      spentGreetings: new WeakSet(),
      message_to_send: '',
      internal_follow_up_user_message: this.local_follow_up_user_message,
      composerSentFor: null,
      lock_interface,
      showLeftActionButton,
      showRightActionButton,
      LeftActionButton,
      RightActionButton,
      StaticSuggestions,
      ChatInstructions,
      disable_insight_help:true,
      screenWidth: window.innerWidth,
      // Set when the seeker taps "Talk it through" — lets the follow-up input
      // show while the reading is still on screen (readingActive) so they can
      // actually answer the reader's question.
      chatAboutOpen: false,
      // The Mystic closed the talk and the seeker tapped "Keep talking" to
      // pick it up again; cleared by her next turn (the server decides again).
      talkResumed: false,
      // The Mystic's lines in the chat when the reply went out: more than
      // this and the words have started to land (listening → writing).
      replyBase: null,
    };
  },
  watch: {
    // The lesson's chips fade up (the card has written in): bring them into view.
    lessonChipsUp(up) { if (up) this.revealLessonChips() },
    'talk.used'() { this.talkResumed = false },
    // The write-back opened in place (DESIGN-2 §39): the chat scrolls only
    // enough to bring Send into view; the top pane does not move.
    composerPrefill: { handler() { this.applyComposerPrefill() }, immediate: true },
    composerVisible() { this.applyComposerPrefill(); this.trySendComposer() },
    composerSend: { handler() { this.trySendComposer() }, immediate: true },
    'messages.length'() { this.trySendComposer() },
    composerFocusSignal(n) {
      if (!n) return
      this.$nextTick(() => {
        const ta = this.$el && this.$el.querySelector && this.$el.querySelector('.follow-up-container textarea')
        if (ta) { try { ta.focus({ preventScroll: true }) } catch (e) { ta.focus() } }
      })
    },
    giftReplyOpen(open) {
      if (open) this.$nextTick(() => this.revealGiftReply())
    },
    // "A reading for someone": open the toggle on "for someone" the moment it
    // appears (immediate: it can already be showing when the preset arrives).
    intentPresetReady: {
      immediate: true,
      async handler(ready) {
        if (!ready) return
        this.intentPresetDone = true
        await this.intentSetSomeone()
        // "Send Sam one back": the name is known. A friend by that name is
        // picked (so the package is bound to them); otherwise it is typed in.
        const name = (this.intentPresetName || '').trim()
        if (name) {
          const friend = (this.intentFriends || []).find(
            (f) => (f.nick_name || '').toLowerCase() === name.toLowerCase())
          if (friend) this.intentPick(friend)
          else { this.intentName = name; this.intentCommit() }
        }
        this.$emit('intent-preset-applied')
      },
    },
    intentPreset(v) { if (!v) this.intentPresetDone = false },
    preparingVisible: {
      immediate: true,
      handler(on) {
        if (this.preparingTimer) { clearTimeout(this.preparingTimer); this.preparingTimer = null }
        this.preparingStalled = false
        if (on) this.preparingTimer = setTimeout(() => { this.preparingStalled = true }, 25000)
      },
    },
    isFetchingResponse: {
      immediate: true,
      handler(on) {
        if (this.fetchTimer) { clearTimeout(this.fetchTimer); this.fetchTimer = null }
        this.fetchStalled = false
        // A reply that has not landed in 75s is not coming the normal way;
        // hand the composer back rather than hold the seeker hostage.
        if (on) this.fetchTimer = setTimeout(() => { this.fetchStalled = true; this.$emit('updateFetchingResponse', false) }, 75000)
        // The words chosen under a lesson wait for the opener's turn to end.
        else this.trySendComposer()
      },
    },
    // A reply goes out: note how many of the Mystic's lines are already here.
    readerBusy: {
      immediate: true,
      handler(on) { this.replyBase = on ? this.readerLineCount : null },
    },
    // Entering the pause: hold the pane at the top so the centred beat shows.
    inGroundingPause(now) {
      if (now) this.scrollChatBox();
    },
    groundingArrivingNow(now) {
      if (now) this.scrollChatBox();
    },
    local_follow_up_user_message(newVal) {
      this.internal_follow_up_user_message = newVal;
    },
    // Each reading starts with the follow-up input hidden again until the seeker
    // opts in via "Talk it through".
    readingActive() {
      this.chatAboutOpen = false;
    },
    // The reading lands: lead with the last card, clean (chat-pin-edge).
    isReadingReady(ready) {
      if (ready) this.scrollChatBox();
    },
    messages: {
      handler(msgs) {
        this.markSpentGreetings(msgs);
        this.scrollChatBox();
        this.adjustForChatGrowth();
      },
      deep: true
    },
    StaticSuggestions: {
      handler() {
        this.scrollChatBox();
        this.adjustForChatGrowth();
      },
      deep: true
    },
    // Rate / Share unlock (the summary step, or a reopened reading): bring
    // them into view rather than leave them under the fold.
    postReadingMenuShowing(now) {
      if (now) this.scrollChatBox();
    },
    // The end-of-reading rating arrives: bring it into view with the chips under it.
    endRating(now) {
      if (now) { this.scrollChatBox(); this.$nextTick(() => this.scrollChatBox()); }
    },
    // When the inline rating row reveals, scroll it into view.
    rateOpen(val) {
      if (val) {
        this.scrollChatBox();
        this.$nextTick(() => this.scrollChatBox());
      }
    }
  },
  mounted() {
    this.scrollChatBox();
    window.addEventListener("resize", this.updateScreenWidth);
    window.addEventListener('reading-follow-up-shown', this.scrollOnFollowUp);
  },
  beforeUnmount() {
    if (this.preparingTimer) clearTimeout(this.preparingTimer);
    if (this.fetchTimer) clearTimeout(this.fetchTimer);
    window.removeEventListener("resize", this.updateScreenWidth);
    document.removeEventListener('click', this.closeExitMenuOutside);
    window.removeEventListener('reading-follow-up-shown', this.scrollOnFollowUp);
  },
  computed: {
    // Whose voice reads a pulled card ("<Mystic> is reading it for you…").
    talkReaderName() {
      const st = (this.state && this.state.store) || {};
      const lead = st.gift_lead && st.gift_lead.letter;
      return st.current_reader_name || (lead && lead.reader_name) || '';
    },
    preparingVisible() {
      const msgs = Array.isArray(this.messages) ? this.messages : []
      return !msgs.length && !(this.showMenuCards && this.StaticSuggestions && this.StaticSuggestions.length)
    },
    // The last thing the Mystic said, with the offered paths as links in it;
    // whatever the sentence does not contain stays a chip underneath.
    inlinedIndex() {
      const ms = Array.isArray(this.messages) ? this.messages : []
      for (let i = ms.length - 1; i >= 0; i--) {
        const m = ms[i]
        // The app's own notes ("Sam wrote back.", a quest line) are not the
        // Mystic's turn: the paths she offered stay on her line, not on them.
        if (m && m.text && !m.imageUrl && m.type !== 'user' && !APP_NOTES.has(m._context)) return i
      }
      return -1
    },
    inlined() {
      // A lesson opener's starters, once spent, never come back (lesson ask path #3).
      const list = liveReplies(Array.isArray(this.StaticSuggestions) ? this.StaticSuggestions : [], this.messages)
      const m = this.inlinedIndex >= 0 ? this.messages[this.inlinedIndex] : null
      return inlineChoices(m ? m.text : '', [...list, ...this.giftReplyActions])
    },
    // The room's replies ("What do Sam's stars say about us?", 2026-10-27): a
    // question they would ask is a link in the Mystic's last line, not a chip.
    giftReplyActions() {
      return (this.giftChips || []).filter(c => c && c.reply)
        .map(c => ({ button_label: c.label, reply: true, inline: true, gift_chip: c.key }))
    },
    // The room's tools stay chips: Draw my card, Tell Sam what it meant,
    // Ask my own question, the Mirror.
    giftToolChips() {
      return (this.giftChips || []).filter(c => c && !c.reply)
    },
    chipActions() {
      const list = this.inlined.leftover
      // Stage 4 (R7.5): her reading for someone ends on one chip, the send.
      if (this.state?.store?.intro_people_reading && (list || []).some(a => a && a.action_type === 'share_reading')) {
        return peopleReadingActions(list)
      }
      // Drawing has one home in the interview: the "Draw the cards" chip,
      // added once the cards are ready (2026-10-27).
      const chips = this.drawCardsOffer ? withoutSelectCards(list, this.state?.store?.conversation_state) : list
      const draw = drawCardsChip({ offered: this.drawCardsOffer, ready: this.drawCardsReady,
        readerAction: this.readerSelectCardsAction, state: this.state?.store?.conversation_state })
      const tools = withoutOpenTalkChip(chips, { composerOpen: this.talkComposer })
      // The primary leads (2026-10-29): Draw the cards, then Change the subject.
      return primaryFirst(draw ? [...tools, { ...draw, _draw: true }] : tools)
    },
    // A row led by Draw the cards: the draw is gold-filled, the rest are ghosts.
    rowHasFill() {
      return !this.lessonRow && !this.giftEnd && this.rowActions.some(a => a && a._draw)
    },
    // Talk it through: where the conversation after the reading stands (the
    // server's word, store.talk).
    talk() {
      return talkFor(this.state?.store?.talk, this.state?.store?.latest_reading_session)
    },
    // The reply box is open for the conversation: tapped open here or from the
    // end-of-reading menu (talkOpen), while turns remain.
    talkComposer() {
      return talkComposerOpen({ open: this.chatAboutOpen || this.talkOpen, talk: this.talk, resumed: this.talkResumed })
    },
    // 4e: the conversation is over (turns used, or the Mystic closed it):
    // the end chips Keep talking · Share · Finish stand under the last reply.
    talkIsOver() {
      return !!this.talkOver && talkEnded(this.talk, { resumed: this.talkResumed })
    },
    // Talk it through (Chris, 2026-10-30): the reading's last card (the
    // advice card) sat full size over the Mystic's new question and pushed it
    // to the foot of the pane. While talking it through, and after, it is a
    // one-line reminder (.cr-talk-recap). -1 when no reading is on screen.
    talkRecapIndex() {
      if (!this.readingActive || !(this.talkComposer || this.talkIsOver)) return -1
      const ms = Array.isArray(this.messages) ? this.messages : []
      for (let i = ms.length - 1; i >= 0; i--) {
        const m = ms[i]
        if (m && m.imageUrl && !m._context && !m._holdForReading && !m._holdForSummary) return i
      }
      return -1
    },
    // The talk's own "…" menu (4c, 4d): Share and Pull a card live there
    // while the reply box is open, not under every reply.
    talkMenuOpen() {
      return !!(this.talkComposer && this.talkOver && this.talk)
    },
    talkShareAction() {
      if (!this.talkMenuOpen || !this.seekerLoggedIn) return null
      const s = StaticSuggestions.value
      return Array.isArray(s) ? (s.find(a => a && a.action_type === 'share_reading') || null) : null
    },
    talkPullMenuAction() {
      if (!this.talkMenuOpen || !talkPullAllowed(this.talk)) return null
      const s = Array.isArray(StaticSuggestions.value) ? StaticSuggestions.value : []
      const kind = (s.find(a => a && a.conversation_type) || {}).conversation_type
        || this.state?.store?.conversation_state
      return talkPullAction(s, kind)
    },
    // The Mystic's quick actions in the "…" menu, without the chips on
    // screen (hand test 2026-10-26 #3).
    menuQuickActions() {
      // A reply is never a menu item: it is a link in the Mystic's line (2026-10-27).
      return menuWithoutChips(this.menuList(this.quickActions), StaticSuggestions.value).filter(a => !isReply(a))
    },
    // Anything at all to list: no "…" that opens onto nothing.
    menuHasItems() {
      return !!(this.finishAction || this.talkShareAction || this.talkPullMenuAction || this.showExitReading
        || this.menuList(LeftActionButton.value).length || this.menuList(RightActionButton.value).length
        || this.menuQuickActions.length)
    },
    // What the "…" menu lists already (Finish, Share, Pull a card), so the
    // lists under them never repeat it while the talk's menu is open.
    talkMenuShown() {
      return [this.finishAction && 'exit_reading', this.talkShareAction && 'share_reading',
        this.talkPullMenuAction && 'talk_pull_card'].filter(Boolean)
    },
    talkCountdown() {
      // From a lesson the count shows from the start: "5 replies" (E2).
      return this.talkComposer && this.talkOver ? talkCountdownOf(this.talk, { before: this.talkSurface }) : null
    },
    // Talking over something: a reading on screen, or a lesson's card or talk.
    talkOver() {
      return !!(this.readingActive || this.talkSurface)
    },
    isChatLocked() {
      return lock_interface.value;
    },
    anyNameShort() {
      return (this.messages || []).some(m => (m.name_options || []).some(
        o => this.nameOptionState(o).state === 'short'));
    },
    /** Anything the seeker could press right now. */
    hasAnyAction() {
      const staticCount = Array.isArray(StaticSuggestions.value)
        ? StaticSuggestions.value.length
        : (StaticSuggestions.value ? 1 : 0);
      const inChat = (this.messages || []).some(
        m => (m.action_buttons && m.action_buttons.length)
          || (m.name_options && m.name_options.length));
      return !!(staticCount
        || inChat
        || (this.quickActions || []).length
        || (this.bottomSuggestions || []).length
        || showLeftActionButton.value
        || showRightActionButton.value);
    },
    // Stage 2: the Mystic's "What landed?" or "I've kept these for you." is the
    // last thing said (or her line is being asked for): the chat pins to it.
    stageTwoTail() {
      if (this.reflectOpen || this.someoneOpen) return true
      const ms = Array.isArray(this.messages) ? this.messages : []
      const last = ms[ms.length - 1]
      return !!last && ['what-landed', 'kept-line', 'stage-four', 'who-ask'].includes(last._context)
    },
    composerVisible() {
      if (this.talkPicking) return false;
      // "What landed?" (R5.4) asks her for one line: the Respond box is open
      // for it even over a delivered reading whose state locks the chat.
      // "Someone else" (R7.3) asks her who, the same way.
      if (this.reflectOpen || this.someoneOpen) return true;
      return showComposer({
        standalone: this.standalone,
        chatLocked: this.isChatLocked,
        // TAROT_N_CARD_DRAW is also the state of a delivered reading: once the
        // seeker opens "Talk it through" over it, nothing is being picked (a
        // card pulled mid-conversation locks the chat while its picker is up).
        pickingCards: this.isPickingCards && !(this.talkComposer && this.talkOver),
        readingActive: this.readingActive,
        // Draw deeper asks her a question over a finished reading: she answers here.
        chatAboutOpen: this.talkComposer || this.giftComposer,
        hasActions: this.hasAnyAction,
        deckOnScreen: this.deckOnScreen,
      });
    },
    /** Is the card grid actually rendered? Card-pick hides the composer
        because the DECK is the way out; no deck means no way out. */
    deckOnScreen() {
      return !!(this.deckShown || this.state?.store?.card_deck?.length || currentWidget.value);
    },
    // Reading is fully delivered — drives whether the post-reading menu
    // (StaticSuggestions) is allowed to show while the reading panel is up.
    // A computed (not a data() ref) so it reliably tracks the shared ref.
    isReadingReady() {
      return readingIsReady.value;
    },
    // The Finish Reading action (exit_reading), surfaced in the top control
    // panel instead of as a chat pill. Present once a reading is delivered.
    drawCardsOffer() {
      if (this.standalone) return false
      // The gift room's way to a card is its own chip (R4.26, R4.27), never Select Cards.
      if (this.giftInv) return false
      return drawCardsOffered({ state: this.state?.store?.conversation_state })
    },
    // The reader's own Select Cards, wherever it painted it (chips or nav):
    // it folds into the composer button rather than standing top right.
    readerSelectCardsAction() {
      return readerSelectCards([
        ...(Array.isArray(StaticSuggestions.value) ? StaticSuggestions.value : []),
        ...(Array.isArray(RightActionButton.value) ? RightActionButton.value : []),
        ...(Array.isArray(LeftActionButton.value) ? LeftActionButton.value : []),
      ])
    },
    // Select Cards has one home in the interview, the "Draw the cards" chip
    // (2026-10-27): never in the nav as well.
    navRightActions() {
      const list = Array.isArray(RightActionButton.value) ? RightActionButton.value : []
      return this.drawCardsOffer ? withoutSelectCards(list, this.state?.store?.conversation_state) : list
    },
    seekerTurns() {
      // What was said in the gift room before their own reading (dimmed) is not a turn of it.
      // nor is a room chip's echo ("Ask my own question").
      return (this.messages || []).filter(m => m && m.type === 'user' && !m._faded && m._context !== 'gift-echo').length
    },
    intentToggleOn() {
      // Their own reading from the gift room is "just for you" (DESIGN-2 §40): no for me / for someone.
      if (this.intentHidden) return false
      return intentToggleVisible({
        state: this.state?.store?.conversation_state, seekerTurns: this.seekerTurns,
        readingActive: !!this.readingActive,
      })
    },
    intentPresetReady() {
      return this.intentPreset === 'someone' && this.intentToggleOn && !this.intentPresetDone
    },
    intentMatches() {
      return intentSuggestions(this.intentFriends, this.intentSent_, this.intentName)
    },
    // The reader is mid-answer: the chat round-trip is out, or words are still
    // streaming in. The cards wait for the reply to land.
    readerBusy() {
      return !!this.isFetchingResponse || !!this.state?.store?.is_streaming_chat
    },
    drawCardsReady() {
      return drawCardsReady({ pace: this.interviewPace, answersBeforeCards: this.interviewAnswersBeforeCards,
                              seekerTurns: this.seekerTurns, busy: this.readerBusy,
                              readerOffers: !!this.readerSelectCardsAction })
    },
    finishAction() {
      // Finish closes the OWNER's reading; a visitor on a link has no such thing.
      if (store.link_holder) return null;
      const s = StaticSuggestions.value;
      return Array.isArray(s) ? s.find(a => a && a.action_type === 'exit_reading') : null;
    },
    // The end-of-reading chip row renders (the same condition as its block).
    chipRowVisible() {
      // "What landed?" holds the row back while it waits for her line. Once the
      // Journal line has come (she said nothing for a while), the row with
      // Finish reading returns under it, the Respond box still open: without
      // it a reading left unanswered had no way out (loop walk 2026-10-26).
      if (this.reflectOpen && !this.keptLineSaid) return false
      return !this.menuCardsShown && this.chipActions.length > 0
        && (!this.readingActive || (this.isReadingReady && this.summarySeen));
    },
    keptLineSaid() {
      return (Array.isArray(this.messages) ? this.messages : []).some(m => m && m._context === 'kept-line');
    },
    // Finish shows in exactly one place (DESIGN 2026-10-22 §4): in the end row
    // when that row shows it, else top right (the reading pane up before the
    // summary, Talk it through's reply box, a card being picked, signed out).
    rowShowsFinish() {
      return !!(this.chipRowVisible && this.rowActions.some(a => a && a.action_type === 'exit_reading' && this.isActionVisible(a)));
    },
    showTopFinish() {
      return !!this.finishAction && !this.rowShowsFinish;
    },
    // A reading made for someone, back from the send modal after it went out:
    // the chat ends with one block (the rating while unrated, Finish reading,
    // Read <Name>'s gift). Before sending the end is as it was.
    giftEnd() {
      const g = this.state?.store?.reading_gift;
      // Every reading made for someone, her people's too (decision 12, 2026-10-26):
      // the rating sits here, after the send, never on the send screen.
      return !!(g && g.is_gift && g.sent && this.readingActive && !this.talkComposer
        && this.chipActions.some(a => a && a.action_type === 'share_reading'));
    },
    giftEndRated() {
      const key = this.state?.store?.latest_reading_session || '';
      return Number(((this.state?.store?.reading_ratings) || {})[key] || 0) > 0;
    },
    // Open while unrated; rated here, it stays with the answer.
    giftEndRating() {
      return this.giftEnd && this.seekerLoggedIn && (!this.giftEndRated || this.giftRatedHere);
    },
    // The row's buttons: in the gift's end block Finish leads, then the gift;
    // its "Rate this reading" chip goes (the rating is open above them).
    // The row after the card drawn from a lesson: its one step (2026-10-28).
    lessonRow() {
      return !this.giftEnd && isLessonToolRow(this.chipActions);
    },
    // Held (kept in place, unseen) until the lesson's card has written in.
    chipsHeld() {
      return lessonChipsHeld(this.messages, new Set(this.writtenTalkCards));
    },
    lessonChipsUp() {
      return !!(this.lessonRow && this.chipRowVisible && !this.chipsHeld);
    },
    rowActions() {
      const list = this.chipActions || [];
      // After a lesson's card: one next step (2026-10-28, API.md 6.4 v1.6).
      if (this.lessonRow) return lessonOneStep(list);
      if (!this.giftEnd) return list;
      const pick = (t) => list.filter(a => a && a.action_type === t);
      return [...pick('exit_reading'), ...pick('share_reading')];
    },
    // Is the seeker signed in? Rate/Share are wired to the logged-in querent;
    // a logged-out viewer gets a Login CTA in their place.
    seekerLoggedIn() {
      return !!store.querent_id;
    },
    // Does the post-reading menu offer Rate/Share? (Used to know when to show
    // the Login CTA in their place for logged-out viewers.)
    // A reading made for yourself has no Rate chip (its rating sits under the
    // summary, 2026-10-19), so its login button only speaks of sharing.
    loginCtaLabel() {
      const s = StaticSuggestions.value;
      const rate = Array.isArray(s) && s.some(a => a && a.action_type === 'rate_reading');
      return loginCtaLabelFor({ rate, linkHolder: !!store.link_holder });
    },
    postReadingHasRateShare() {
      const s = StaticSuggestions.value;
      return Array.isArray(s) && s.some(
        a => a && (a.action_type === 'rate_reading' || a.action_type === 'share_reading'));
    },
    // The reading has landed and the chat is showing its post-reading menu
    // (the same condition the static_suggestions block renders under).
    postReadingMenuShowing() {
      return !!(this.readingActive && this.isReadingReady && this.summarySeen
        && this.postReadingHasRateShare);
    },
    // Doorman v1.2 — show the ← Menu button when the seeker is in
    // a Doorman-dispatched state that isn't part of the reading
    // flow. Reading-flow states (SELECTING_CARDS / TAROT_N_CARD_DRAW
    // / legacy GAME_TAROT_4_CARDS) have their own UX and would be
    // crowded by an extra nav button.
    showDoormanBackButton() {
      if (!this.state || !this.state.store) return false
      const cs = this.state.store.conversation_state
      const eligibleStates = new Set([
        'RECEPTION',
        'EDUCATION',
        'ASTROLOGY_REGISTRATION',
        'PERSONALIZED_ASTROLOGY',
      ])
      return eligibleStates.has(cs)
    },
    // "Exit Reading" — the conversational pre-card phase (grounding /
    // interview / funnel) is the avatar-focus surface and has no other
    // way out (the ← Menu trigger isn't handled there, and the reading-
    // flow states own their own exits). Give the seeker a one-tap exit
    // back to the reader's Doorman menu.
    // The box's placeholder on one line: under 360px "Ask Mystic Aida
    // anything…" wrapped and was clipped (gift bug walk 2026-10-25 #33).
    placeholderShown() {
      // The gut line before the draw (journal practice 2026-10-28): "My gut says…" while it is asked.
      const p = gutAskOpen(this.messages) ? GUT_PLACEHOLDER : (this.composerPlaceholder || 'Respond')
      return (this.screenWidth < 360 && p.length > 18) ? 'Ask anything…' : p
    },
    showExitReading() {
      if (!this.state || !this.state.store) return false
      // The gift room is not a reading to exit: its session can be left in
      // INTERVIEW from her own reading (gift bug walk 2026-10-25 #7, #37).
      if (this.giftInv) return false
      // Her own reading from the room has its own way back ("‹ Sam's gift"),
      // and the pill sat on the chat's first line (bug walk #37).
      if (this.intentHidden) return false
      const cs = this.state.store.conversation_state
      return new Set(['TAROT_FUNNEL', 'INTERVIEW', 'GROUNDING']).has(cs)
    },
    // Subscribers don't spend per-reading emeralds (their subscription
    // covers it), so the price tag on menu cards is misleading for them.
    isSubscribed() {
      return !!(this.state && this.state.store && this.state.store.querent_subscribed)
    },
    // Show the Reading History link only when the seeker has at
    // least one prior reading on record. Avoids painting a link
    // that opens to an empty modal for first-time seekers. Also
    // hides during any in-session state (funnel / picking cards /
    // active reading / post-reading menu) — the link would compete
    // with the right-action button there, and history isn't useful
    // to surface while a reading is in flight.
    hasReadingHistory() {
      if (!this.state || !this.state.store) return false
      // A reading is on screen — keep the history link out of it regardless
      // of what the post-delivery conversation_state reports.
      if (this.readingActive) return false
      const inSessionStates = new Set([
        'TAROT_FUNNEL',
        'INTERVIEW',
        'GROUNDING',
        'SELECTING_CARDS',
        'TAROT_N_CARD_DRAW',
        'GAME_TAROT_4_CARDS',
        'READING_CONCLUSION',
        'RECEPTION',
        // Education is a focused session too — keep the history link out of it.
        'EDUCATION',
      ])
      if (inSessionStates.has(this.state.store.conversation_state)) return false
      const sessions = this.state.store.reading_sessions
      return Array.isArray(sessions) && sessions.some(s => s && s.initial_question)
    },
    // Their own reading from the gift room (intentHidden), on a phone: the
    // session label as its own row (gift room walk #5). Not on a lesson's
    // card, which hides the label on purpose.
    // Every session path at 390 now, not only that one (2026-10-30).
    phoneSessionLabel() {
      return !!(this.statusShown && !this.lessonStage)
    },
    // The Mystic's lines on screen (held ones are not on screen yet).
    readerLineCount() {
      return (Array.isArray(this.messages) ? this.messages : [])
        .filter((m) => m && m.type !== 'user' && m.text && !m._holdForReading).length
    },
    // What the chat knows for the status line, with the page's cues.
    statusCues() {
      const page = this.sessionCues || {}
      const grounding = this.inGroundingPause || this.groundingArrivingNow
      const busy = !!this.readerBusy && !grounding
      const cardReading = (Array.isArray(this.messages) ? this.messages : []).some((m) => m
        && m._context === 'talk-card' && m._live && m.talk_card && !m.talk_card.reading)
      const talking = !!(this.talkComposer && this.talkOver)
      return {
        reader: this.talkReaderName,
        instructions: this.ChatInstructions,
        hidden: !!(this.standalone || page.quietPick),
        busy,
        writing: busy && this.replyBase !== null && this.readerLineCount > this.replyBase,
        picking: !!this.deckShown,
        // A card pulled while talking (or a lesson's) is one card.
        pickCount: this.talkPicking ? 1 : page.pickCount,
        picked: this.talkPicking ? 0 : page.picked,
        readingCards: !!page.readingCards,
        cardReading,
        painting: !!page.painting,
        talk: talking ? { text: (this.talkCountdown && this.talkCountdown.text) || '' } : null,
        giftFor: page.giftFor || this.intentSent || '',
        giftFrom: this.giftInv ? (this.giftSender || '') : '',
      }
    },
    status() {
      return sessionStatus(this.statusCues)
    },
    statusShown() {
      return !!(this.status.text && this.inReadingSession)
    },
    // The "In session with …" pill belongs only to an actual reading session —
    // the funnel/interview/card-pick/draw/reading flow — NOT the Doorman menu or
    // the idle reader-detail/deck browse surface.
    inReadingSession() {
      if (this.readingActive) return true
      const cs = this.state?.store?.conversation_state
      if (!cs) return false
      return new Set([
        'TAROT_FUNNEL', 'INTERVIEW', 'GROUNDING', 'SELECTING_CARDS',
        'TAROT_N_CARD_DRAW', 'GAME_TAROT_4_CARDS', 'READING_CONCLUSION',
        'RECEPTION', 'EDUCATION',
      ]).has(cs)
    },
    thinkingMessage() {
      return loadingHeadsUpMessage.value || 'One moment…';
    },
    // Before the grounding line's first word: the arriving dots.
    groundingArrivingNow() {
      // Once per reading: never with a name on it (the server sends no frame then either).
      return !!groundingArriving.value && !this.skipGrounding && !this.openingHeld && !this.intentSent;
    },
    // The grounding beat: we're waiting AND the last message is the reader's
    // grounding greeting (salutation-message). During this the backend holds
    // ~10s (GROUNDING_PAUSE_SECONDS) before advancing to the funnel — show the
    // depleting countdown ring instead of the generic spinner.
    // Once per reading (Chris, 2026-10-19): a greeting that has been followed
    // by another line is spent, and a reading with a name on it has no beat.
    // Asking the opening question again (a name chosen after it) takes the old
    // question out while a reply is out, which left the greeting last and
    // fetching: the beat, the glow and the ring came back. They no longer do.
    inGroundingPause() {
      if (!this.isFetchingResponse || this.skipGrounding || this.openingHeld || this.intentSent) return false;
      const msgs = this.messages;
      if (!Array.isArray(msgs) || !msgs.length) return false;
      const last = msgs[msgs.length - 1];
      return !!last && last.type === 'reader' && last._context === 'salutation-message'
        && !(this.spentGreetings && this.spentGreetings.has(last));
    },
    // Render the start-menu quick actions as large image cards (only in the
    // Doorman entry state). Every other state keeps the compact pill row.
    showMenuCards() {
      // The gift room opens in the Doorman state, but it is a conversation, not
      // the menu: its lines are ordinary bubbles (2026-10-22 §5, mockup R1).
      return this.state?.store?.conversation_state === 'DOORMAN' && !this.giftInv;
    },
    // The chat dressed as the Doorman's menu (dark surface, the greeting as a
    // centred Cinzel header). Never under a reading: a finished reading
    // reopened from its link runs the reader's welcome first, which leaves the
    // store in the Doorman state while the reading loads over it, and every
    // replayed line became a caps headline (Chris, 2026-10-29). The reading's
    // chat is a conversation.
    menuSurface() {
      return !!(this.showMenuCards && !this.readingActive);
    },
    // The Doorman's cards are on screen. Only then does the surface hold
    // still (they fit, and a scrollbar shifted them off centre). The gift room
    // opens in the Doorman state with no cards and a thank-you that does not
    // fit: holding still there cut off its line and Send (2026-10-22, Chris).
    menuCardsShown() {
      return !!(this.showMenuCards && this.StaticSuggestions && this.StaticSuggestions.length && !this.readingActive);
    },
    // Card-pick phase (deck on screen, seeker picking cards) — hide the Respond
    // input: there's nothing to type while picking, and it showed on the resume
    // path where lock_interface wasn't set. Covers the reveal too (state stays
    // SELECTING_CARDS through the draw).
    isPickingCards() {
      const cs = this.state?.store?.conversation_state;
      return cs === 'SELECTING_CARDS' || cs === 'TAROT_N_CARD_DRAW'
        || cs === 'GAME_TAROT_4_CARDS';
    },
    currentWidget() {
      return currentWidget.value;
    },
    pluginWidgetComponent() {
      const widget = currentWidget.value;
      if (!widget) return null;
      const builtinTypes = ['birthdate', 'birthplace_map', 'collect_email_form', 'countdown_timer'];
      if (builtinTypes.includes(widget.type)) return null;
      const entry = pluginRegistry.widgets[widget.type];
      return entry ? entry.component : null;
    },
  },
  methods: {
    // Never over words she has already typed; handed back once used.
    applyComposerPrefill() {
      const words = String(this.composerPrefill || '').trim()
      if (!words || !this.composerVisible) return
      if (!String(this.internal_follow_up_user_message || '').trim()) this.internal_follow_up_user_message = words
      this.$emit('composer-prefilled')
      this.$nextTick(() => {
        const ta = this.$el && this.$el.querySelector && this.$el.querySelector('.follow-up-container textarea')
        if (ta) { try { ta.focus({ preventScroll: true }) } catch (e) { ta.focus() } }
      })
    },
    // The words chosen under the lesson go as the first message, once.
    trySendComposer() {
      const ask = this.composerSend
      if (!ask || ask === this.composerSentFor) return
      if (!firstWordsDue({ pending: ask, messages: this.messages, fetching: this.isFetchingResponse, composerVisible: this.composerVisible })) return
      this.composerSentFor = ask
      const words = String(ask.words || '').trim()
      this.$emit('composer-sent')
      const action = ask.how === 'reply' ? starterAction(this.quickActions, words) : null
      if (action) { this.selectQuickAction(action); return }
      this.internal_follow_up_user_message = words
      this.sendChat()
    },
    // "Mystic Aida is reading your Past card" while a reveal's headline comes.
    revealReadingLine(message) {
      return readingLine(this.talkReaderName, positionOf(message && message.card));
    },
    talkPanelLabel(card) { return talkPanelLabelOf(card); },
    talkQuestionLabel(card) { return panelQuestionLabel(card); },
    // The write-back's card (DESIGN-2 §39): scroll only enough to bring the
    // whole card, its foot ("Not now") and a margin under it, into view, never
    // past its top; the top pane does not move. Bringing only "Not now" to the
    // very edge left the card's foot cut off (gift bug walk 2026-10-25 #28).
    revealGiftReply() {
      const comp = this.$refs.giftReplyRef
      const el = comp && comp.$el
      if (!el || !el.getBoundingClientRect) return
      const chat = this.$refs.chat_container_ref
      const box = chat && chat.closest && chat.closest('.chat-box-container')
      for (const sc of [chat, box]) {
        if (!sc || !sc.getBoundingClientRect) continue
        const up = menuOverflow(el.getBoundingClientRect(), sc.getBoundingClientRect(), 16)
        if (up > 0) sc.scrollTop += up
      }
    },
    // Their own reading's starters (API-2 §5) are their own words, in their case.
    isVoiceStarter(action) {
      return !!action && (action.focus_composer || (action.branch_index !== undefined && action.branch_index !== null))
    },
    // The pulled card's art opens the card's detail (its traditional meaning).
    // A lesson's card has written its reading in: its chips fade up.
    onTalkCardWritten(card) {
      const id = card && card.id;
      if (id && !this.writtenTalkCards.includes(id)) this.writtenTalkCards = [...this.writtenTalkCards, id];
    },
    // The chips have come up under the lesson's card: the chat (never the
    // page) scrolls just enough for the whole row to be in view.
    revealLessonChips() {
      this.$nextTick(() => {
        const box = this.$refs.chat_container_ref;
        const row = box && box.querySelector && box.querySelector('.message-box.lesson-chips');
        if (!box || !row || typeof box.scrollTo !== 'function') return;
        const over = row.getBoundingClientRect().bottom + 12 - box.getBoundingClientRect().bottom;
        if (over <= 0) return;
        const reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        box.scrollTo({ top: box.scrollTop + over, behavior: reduced ? 'auto' : 'smooth' });
      });
    },
    openTalkCardDetail(card) {
      if (!card) return;
      const st = (this.state && this.state.store) || {};
      openCardDetailFor({ reader: st.current_reader_subdomain, card: card.card, reversed: !!card.reversed,
        position: card.kind === 'gift' ? 'Your card' : 'Pulled' });
    },
    // The grounding greeting gets the large, centred beat while it is the
    // last line; afterwards it settles into the transcript. A reading for
    // someone never gets the beat (the server sends no greeting there; this is
    // the backup, Chris 2026-10-19).
    groundingBeatClass(message, index) {
      if (message._context !== 'salutation-message') return '';
      // No beat at all when the entry was held for a name or a name is on the
      // reading; none again for a greeting that has had its one.
      if (this.skipGrounding || this.openingHeld || this.intentSent) return 'was-grounding-beat';
      if (this.spentGreetings && this.spentGreetings.has(message)) return 'was-grounding-beat';
      return index === this.messages.length - 1 ? 'is-grounding-beat' : 'was-grounding-beat';
    },
    // A grounding greeting has had its beat once anything followed it. Kept by
    // identity (spentGreetings), so a new greeting (a genuine Start Over)
    // still gets its own.
    markSpentGreetings(msgs) {
      if (!Array.isArray(msgs)) return;
      let after = false;
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i];
        if (!m) continue;
        if (m._context === 'salutation-message' && after) this.spentGreetings.add(m);
        after = true;
      }
    },
    nameOptionState(opt) {
      return nameOptionStateOf(opt, {
        balance: this.carats, currentSlug: this.currentSlug,
        pricesVisible: (this.state && this.state.store ? this.state.store.prices_visible : true) !== false,
      });
    },
    // Clamp the length input to its valid range (validation on blur). Empty /
    // non-numeric falls back to the minimum.
    clampLength(value) {
      let n = parseInt(value, 10);
      if (isNaN(n)) n = this.lengthRange[0];
      return Math.max(this.lengthRange[0], Math.min(this.lengthRange[1], n));
    },
    // ── Menu card helpers ──────────────────────────────────────────
    // Title without the trailing "(NN ✦)" price suffix.
    actionTitle(label) {
      if (!label) return '';
      return String(label).replace(/\s*\(\s*[\d,]+\s*✦\s*\)\s*/g, '').trim();
    },
    // The emerald price embedded in the label, or null when free.
    actionPrice(label) {
      if (!label) return null;
      const m = String(label).match(/([\d,]+)\s*✦/);
      return m ? m[1] : null;
    },
    // Default visual (gradient class + icon) per action type. These are
    // the front-end defaults shown until a custom card image is set in
    // the Studio (wiring TBD).
    actionVisual(action) {
      const k = String((action && (action.key || action.action_type)) || '').toLowerCase();
      const lbl = String((action && action.button_label) || '').toLowerCase();
      if (k.includes('tarot') || lbl.includes('tarot') || lbl.includes('pull')) return { cls: 'is-tarot', icon: 'fa-layer-group' };
      if (k.includes('astro') || lbl.includes('astro') || lbl.includes('horoscope')) return { cls: 'is-astro', icon: 'fa-moon' };
      if (k.includes('education') || lbl.includes('practice') || lbl.includes('learn')) return { cls: 'is-practice', icon: 'fa-book-open' };
      if (k.includes('continue') || lbl.includes('continue')) return { cls: 'is-generic', icon: 'fa-bookmark' };
      return { cls: 'is-generic', icon: 'fa-wand-magic-sparkles' };
    },
    // Pick a leading icon for a reading-actions menu item from its action_type
    // / label. Keeps the menu scannable (icon + left-aligned label).
    actionMenuIcon(action) {
      const k = String((action && (action.action_type || action.key)) || '').toLowerCase();
      const lbl = String((action && action.button_label) || '').toLowerCase();
      if (k.includes('exit') || k.includes('finish') || lbl.includes('exit') || lbl.includes('finish') || lbl.includes('done')) return 'fa-arrow-right-from-bracket';
      if (k.includes('reset') || k.includes('subject') || lbl.includes('subject') || lbl.includes('change')) return 'fa-arrows-rotate';
      if (k.includes('select_cards') || lbl.includes('card') || lbl.includes('pull') || lbl.includes('draw')) return 'fa-layer-group';
      if (k.includes('rate') || lbl.includes('rate')) return 'fa-star';
      if (k.includes('share') || lbl.includes('share')) return 'fa-share-nodes';
      if (k.includes('astro') || lbl.includes('astro') || lbl.includes('horoscope')) return 'fa-moon';
      return 'fa-wand-magic-sparkles';
    },
    // Split a quick-action button_label on the ✦ sentinel and return
    // an array of segments. Each segment is either {text: '...'} or
    // {icon: true}, so the template can render an actual emerald gem
    // icon (matching the top-left balance display) in place of the ✦
    // glyph. The BE bakes ✦ into labels like "New Reading (50 ✦)".
    renderActionLabel(label) {
      if (!label) return [{ text: '' }];
      const parts = String(label).split('✦');
      const out = [];
      parts.forEach((p, i) => {
        if (p) out.push({ text: p });
        if (i < parts.length - 1) out.push({ icon: true });
      });
      return out;
    },
    // Doorman v1.2 — back-to-menu click handler. Re-uses the same
    // selectQuickAction pipeline the rest of the chat-container
    // buttons use, so the conversation POST + state-change
    // wiring is unchanged. action_type 'back_to_doorman' matches
    // the BE triggers added in Phase A.
    onBackToDoorman() {
      const cs = (this.state && this.state.store)
        ? this.state.store.conversation_state
        : 'EDUCATION'
      this.selectQuickAction({
        button_label: '← Menu',
        action_type: 'back_to_doorman',
        prompt_instructions: 'Back to menu',
        conversation_type: cs,
      })
    },
    // Doorman v1 — visual emphasis predicate. Returns true when the
    // BE's Interview readiness signal is on AND the action is the
    // one that begins a reading. The Begin Reading button is always
    // rendered and clickable regardless; this method just decides
    // whether to add an emphasis class.
    isReadingActionEmphasized(action) {
      if (!this.state || !this.state.store) return false;
      if (!this.state.store.ready_for_reading) return false;
      const at = action && (action.action_type || action.key);
      // Match any action_type that triggers card selection — covers
      // 'select_cards' (default), 'select_cards_one_card',
      // 'select_cards_three_card', 'select_cards_four_card'.
      return typeof at === 'string' && at.startsWith('select_cards');
    },
    submitEmailViaQuickAction(email) {
      const action = {
        button_label: `${currentWidget.value.payload[0].button_label}`,
        action_type: `${currentWidget.value.payload[0].action_type}`,
        prompt_instructions: `${email}`,
        conversation_type: this.state.store.conversation_state,
        widget_return_value: `${email}`
      };
      this.selectQuickAction(action);
      currentWidget.value = null; // hide widget
    },
    submitPluginWidget(value) {
      const payload = currentWidget.value?.payload;
      if (payload && Array.isArray(payload) && payload[0]) {
        const action = {
          button_label: payload[0].button_label || 'Submit',
          action_type: payload[0].action_type || 'continue_conversation',
          prompt_instructions: String(value),
          conversation_type: this.state.store.conversation_state,
          widget_return_value: String(value),
        };
        this.selectQuickAction(action);
      }
      currentWidget.value = null;
    },
    // The chat's birth steps ask with the one birth component. The date step
    // carries a time now (part of the day / exact / Not sure); the place step
    // completes the same moment, and a signed-in seeker's is saved through the
    // one write path so the profile has it too. The conversation still hears
    // what it always did.
    async onChatBirthMoment(out) {
      this.chatBirthMoment = { birthdate: out.birthdate, time: out.time }
      if (this.seekerLoggedIn) {
        try { await saveBirthMoment(this.chatBirthMoment) } catch (e) { /* the conversation goes on */ }
      }
      this.submitBirthdateViaQuickAction(out.birthdate)
    },
    async onChatBirthplace(out) {
      if (this.seekerLoggedIn && this.chatBirthMoment && out.place) {
        try { await saveBirthMoment({ ...this.chatBirthMoment, place: out.place }) } catch (e) { /* goes on */ }
      }
      this.submitBirthplaceViaQuickAction(out.place)
    },
    submitBirthplaceViaQuickAction(coords) {
      const action = {
        button_label: `${currentWidget.value.payload[0].button_label}`,
        action_type: `${currentWidget.value.payload[0].action_type}`,
        prompt_instructions: `I chose a spot that’s close to where I was born.`,
        conversation_type: this.state.store.conversation_state,
        widget_return_value: `${coords.latitude},${coords.longitude}`
      };
      this.selectQuickAction(action);
      // Hide widget
      currentWidget.value = null;
    },
  submitBirthdateViaQuickAction(birthdate) {
    // Local validation: ensure the date is valid and reasonable
    const dateObj = new Date(birthdate);

    const now = new Date();
    if (
      !birthdate ||
      isNaN(dateObj.getTime()) ||
      dateObj > now
    ) {
      alert("Please select a valid birthdate.");
      return;
    }
    const sunSign = getSunSign(dateObj.getMonth() + 1, dateObj.getDate());
    const action = {
      button_label: `${currentWidget.value.payload[0].button_label}`,
      action_type: `${currentWidget.value.payload[0].action_type}`,
      prompt_instructions: `I'm a ${sunSign} born on ${formatPrettyDate(birthdate)}`,
      conversation_type: this.state.store.conversation_state,
      widget_return_value: birthdate
    };

    this.selectQuickAction(action);
    // Clear the widget after submission
    currentWidget.value = null;
  },
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
    toggleQuickActions() {
    this.showQuickActions = !this.showQuickActions;
    if (this.showQuickActions) {
      // Add click event listener to handle outside clicks
      document.addEventListener('click', this.handleOutsideClick);
    } else {
      document.removeEventListener('click', this.handleOutsideClick);
      }
    },
    handleOutsideClick(event) {
      // Check if the click is outside the dropdown and trigger button
      const quickActionsTrigger = this.$refs.quickActionsTrigger;
      const quickActionsDropdown = this.$refs.quickActionsDropdown;

      if (
        quickActionsTrigger &&
        quickActionsDropdown &&
        !quickActionsTrigger.contains(event.target) &&
        !quickActionsDropdown.contains(event.target)
      ) {
        this.showQuickActions = false; // Hide dropdown
        document.removeEventListener('click', this.handleOutsideClick); // Clean up event listener
      }
    },
    adjustForChatGrowth() {
      // Measure the chat container's height dynamically
      this.$nextTick(() => {
        const chatContainer = this.$refs.chat_container_ref;
        if (chatContainer) {
          const chatHeight = chatContainer.scrollHeight; // Total content height
            //Check screen size
            if (window.innerWidth >= 700) {
              // Only add 125px on larger screens
              adjustPanesForChat(chatHeight + 125);
            } else {
              adjustPanesForChat(chatHeight+50);
            }
          //adjustPanesForChat(chatHeight+125);
        }
      });
    },
    GeneralReaderConversation: ReaderConversation,
    async sendChat(instruction = null, action_type='continue_conversation',
                   conversation_type=state.store.conversation_state, form_value=null) {

      // Use instruction from quick action if provided, otherwise use the chat input field message
      this.$emit('updateFetchingResponse', true);
      this.message_to_send = instruction ?? this.internal_follow_up_user_message;


      // Emit the message to the message list
      if (this.message_to_send !== ''){
        this.$emit('add-message', this.message_to_send);
      }

      // Clear the input field if the message is from the input field (not a quick action)
      if (!instruction) {
        this.internal_follow_up_user_message = '';
      }
      // "What landed?" (R5.4): her line is kept on her reading by the page,
      // never sent to the Mystic as a turn.
      if (this.reflectOpen && !instruction && this.message_to_send.trim()) {
        this.$emit('updateFetchingResponse', false);
        this.$emit('reflect', this.message_to_send);
        this.scrollChatBox();
        return;
      }
      // "Someone else" (R7.3): her line names who; the page starts that reading.
      if (this.someoneOpen && !instruction && this.message_to_send.trim()) {
        this.$emit('updateFetchingResponse', false);
        this.$emit('someone', this.message_to_send);
        this.scrollChatBox();
        return;
      }
      disable_insight_help.value = []
      StaticSuggestions.value = ""
      this.$emit('hideQuickActions');


      // A standalone assistant owns its own conversation: the parent handles
      // the message via `add-message`. Posting it to the READER conversation as
      // well wrote every Altar message into the reader's history and advanced
      // its state machine — which is why typing an affirmation left the reader
      // greeting you "welcome back" from a conversation you never had.
      if (this.standalone) {
        this.$emit('updateFetchingResponse', false);
        this.scrollChatBox();
        return;
      }

      // Her own typed line, for the page to read too (recipient journey R9.4:
      // it may fit another Mystic's practice better). A tap is not hers.
      if (!instruction && !form_value && this.message_to_send.trim()) {
        this.$emit('typed-line', this.message_to_send);
      }
      // Fetch response containing quick actions from the conversation API
      const message = form_value || this.message_to_send;
      const stateBefore = state.store.conversation_state;

      const quickActionsResponse = await ReaderConversation(
          state, message, action_type, conversation_type);

      this.$emit('updateFetchingResponse', false);
      loadingHeadsUpMessage.value = '';
      // The gut line's reply (API.md §1): the server opened the picker on this
      // turn, so the page arms it as a Select cards tap does (prod 2026-10-29).
      if (turnOpensPicker({ actionType: action_type, before: stateBefore, after: state.store.conversation_state })) {
        this.$emit('interfaceTrigger', 'select_cards', { fromTurn: true });
      }

      if (quickActionsResponse && quickActionsResponse.quick_actions && StaticSuggestions) {
        this.$emit('updateQuickActions', quickActionsResponse.quick_actions);
      }

      // Scroll to the bottom of the chat after updating
      this.scrollChatBox();
    },
    // Which post-reading buttons to render. Always hide exit_reading (it lives
    // in the top control panel). For a logged-out viewer also hide Rate/Share —
    // they're attributed to the logged-in seeker and are replaced by a Login CTA.
    // Rate and Share are the two things a landed reading asks of the seeker;
    // they carry the filled treatment so they read as the actions, not chips.
    isPostReadingPrimary(action) {
      return !!action && (action.action_type === 'rate_reading' || action.action_type === 'share_reading' || isLessonPrimary(action));
    },
    // A reading made for someone: "Share Reading" is "Gift to Lark", and once
    // sent, "Read Lark's gift".
    // The "…" menu's lists, without what the talk's own items already say.
    menuList(actions) {
      return talkMenuExtras(actions, { talkMenuOpen: this.talkMenuOpen, shown: this.talkMenuShown })
    },
    actionLabel(action) {
      // After the card drawn from a lesson: GET THE FULL READING · 50 ✦ (lesson-redo E2.6).
      if (action && action.action_type === 'lesson_full_reading') {
        return (this.state && this.state.store && this.state.store.prices_visible === false) ? String(action.button_label || '') : lessonChipLabel(action)
      }
      if (action && action.action_type === 'share_reading') {
        const ga = giftAction(this.state?.store?.reading_gift, { sendIt: !!this.state?.store?.intro_people_reading })
        if (ga) return ga.label
      }
      return toolChipLabel(talkActionLabel(action, { pricesVisible: (this.state && this.state.store ? this.state.store.prices_visible : true) !== false }), action)
    },
    giftAction,
    toolChipLabel,
    lessonChipSegments,
    // The lesson's opener and the hand-off fade in; the opener steps aside
    // while the card drawn from the lesson is on its own (2026-10-28).
    openerClass(message) {
      if (!message) return {};
      return { 'cr-opener-in': message._ref === 'lesson-opener' || message._ref === 'lesson-full', 'cr-lesson-opener': message._ref === 'lesson-opener' };
    },
    keptLineSegments,
    splitElseLine,
    momentSegments,
    navLineSegments,
    // R3.5: a button's words saved as her line before the fix stay hidden.
    isCommandEcho,
    // ── Care Package intent ──
    intentSetMe() {
      if (!this.intentForSomeone) return
      this.intentForSomeone = false
      this.intentEditing = false
      this.intentName = ''; this.intentPicked = ''
      if (this.intentSent || this.openingHeld) { this.intentSent = ''; this.$emit('care-intent', { for_name: '', for_querent_uuid: '' }) }
    },
    async intentSetSomeone() {
      this.$nextTick(() => {
        const box = this.$el && this.$el.querySelector && this.$el.querySelector('.cr-intent-who')
        if (box && box.scrollIntoView) box.scrollIntoView({ block: 'nearest' })
      })
      this.intentForSomeone = true
      this.intentEditing = true
      this.$nextTick(() => this.$refs.intentInput && this.$refs.intentInput.focus())
      if (!this.intentFriends.length && this.state?.store?.session_id) {
        try { this.intentFriends = (await getMyFriends(this.state.store.session_id)).friends || [] } catch (e) { /* no friends list, the box still works */ }
      }
      if (!this.intentSent_.length && this.state?.store?.session_id) {
        try { this.intentSent_ = await careSent(this.state.store.session_id) } catch (e) { /* no history, the box still works */ }
      }
    },
    intentPick(f) {
      this.intentName = f.nick_name
      this.intentPicked = f.querent_uuid || ''
      this.intentCommit()
    },
    intentCommit() {
      this.intentListOpen = false
      const name = (this.intentName || '').trim()
      if (!name) return
      // Done: the box folds into the chip and the cursor lands in Respond,
      // so "what next" is answered by where the focus is.
      this.intentEditing = false
      this.$nextTick(() => { const ta = this.$el && this.$el.querySelector('.follow-up-container textarea'); if (ta) ta.focus() })
      if (name === this.intentSent) return
      this.intentSent = name
      this.$emit('care-intent', { for_name: name, for_querent_uuid: this.intentPicked || '' })
    },
    isActionVisible(action) {
      if (!action) return false;
      // Finish is in the end row whenever that row renders, never while the
      // talk's reply box is open (then it is top right): one place at a time.
      if (action.action_type === 'exit_reading') {
        return this.seekerLoggedIn && !store.link_holder && !(this.talkComposer && this.talkOver);
      }
      // A signed-out visitor on a link: asking is one sign-in away, said by the
      // bar's single "Sign in to ask or share" button.
      if (!this.seekerLoggedIn && store.link_holder && action.action_type === 'chat_about_this') return false;
      if (!this.seekerLoggedIn
        && (action.action_type === 'rate_reading' || action.action_type === 'share_reading')) {
        return false;
      }
      return true;
    },
    // A link inside the Mystic's words. The gift opener's "Something else"
    // sends nothing: it puts her in the Respond box (R4.25). A branch is
    // counted once per tap (R4.32), then answered like any path.
    onInlineChoice(action) {
      // The room's reply ("What do Sam's stars say about us?"): the page says
      // it as their words and answers it (onGiftChip).
      if (action && action.gift_chip) { this.$emit('gift-chip', action.gift_chip); return }
      chooseInlineLink(action, { select: (a) => this.selectQuickAction(a), focusComposer: () => this.focusComposer() })
    },
    inlineTestId(action) {
      if (!action) return null
      if (action.gift_chip) return action.gift_chip
      return action.inline ? 'gift-branch-link' : 'reply-link'
    },
    // "Someone else": the Respond box, focused, for her to say who.
    onSomeoneElse() {
      this.$emit('stage-four-someone')
      this.focusComposer()
    },
    focusComposer() {
      this.$emit('open-composer')
      this.$nextTick(() => {
        const ta = this.$el && this.$el.querySelector && this.$el.querySelector('.follow-up-container textarea')
        if (ta) ta.focus()
      })
    },
    selectQuickAction(action) {
      // What each action means lives in routeQuickAction, because the
      // end-of-reading menu taps the same actions and the two must not drift.
      // This method performs the decision; it no longer makes it.
      // A starter's way out ("Something else on my mind", API-2 §5) sends
      // nothing: it puts them in the reply box.
      if (action && action.focus_composer) { this.focusComposer(); return; }
      // "Get the full reading" (API.md 6.4 v1.5): a regular reading with the
      // Mystic. The chips fade first, then the turn goes; the hand-off line
      // fades in and the composer opens. A tap mid-fade does nothing more.
      if (action && action.action_type === 'lesson_full_reading' && !action._chipsGone) {
        if (this.lessonChipsLeaving) return;
        this.lessonChipsLeaving = true;
        setTimeout(() => {
          this.selectQuickAction({ ...action, _chipsGone: true });
          this.lessonChipsLeaving = false;
        }, chipsLeaveMs({ reduced: prefersReducedMotion() }));
        return;
      }
      const r = routeQuickAction(action);

      if (r.openChatAbout) {
        this.chatAboutOpen = true;
        // "Keep talking" after the Mystic closed it: the reply box reopens.
        if (this.talk && this.talk.closed) this.talkResumed = true;
        // Talk it through: the page keeps the chat (not the options menu) and
        // remembers the reply box is open.
        if (action.conversation_type !== 'DRAW_DEEPER') this.$emit('talk-open');
      }

      // "Keep talking: 5 more": the page pays for it (or opens the store).
      if (r.talkMore) { this.$emit('talk-more', action); return; }

      if (r.showLogin) { this.$emit('update:showLogin', true); return; }

      if (r.exitReading) {
        currentWidget.value = null;
        if (r.clearSuggestions) StaticSuggestions.value = "";
        this.$emit('exitReading', r.exitReading);
        return;
      }

      if (r.preTrigger) this.$emit('interfaceTrigger', r.preTrigger);

      if (!r.trigger) return;

      if (r.hideQuickActions) this.showQuickActions = false;
      document.removeEventListener('click', this.handleOutsideClick);
      // A lesson's "Get the full reading" carries the names of its slots (E2.7).
      // A paused gift's chip carries its gift (continue_gift, 2026-11-01).
      this.$emit('interfaceTrigger', r.trigger, (r.trigger === 'lesson_full_reading' || r.trigger === 'continue_gift') ? action : undefined);

      if (r.chat) {
        this.sendChat(r.chat.instruction, r.chat.actionType,
                      r.chat.conversationType, r.chat.formValue);
      }
    },
    // Rated: the offer to share opens under the crystals, so keep them in view.
    onEndRated(n) {
      if (this.giftEnd) this.giftRatedHere = true;
      this.$emit('reading-rated', n);
      if (n >= 3) setTimeout(() => this.scrollChatBox(), 60);
    },
    // true when the newest thing in the chat is a pulled card's panel (nothing
    // said after it): it leads once, and is then left where it is.
    // The Mystic's own lines after the newest pulled card (a quest line is a
    // note, not a turn: it does not count).
    linesAfterLastTalkCard() {
      const ms = Array.isArray(this.messages) ? this.messages : [];
      let at = -1;
      for (let i = ms.length - 1; i >= 0; i--) { if (ms[i] && ms[i]._context === 'talk-card') { at = i; break; } }
      if (at < 0) return 0;
      return ms.slice(at + 1).filter(m => m && m.type === 'reader' && m._context !== 'quest-line'
        && (String(m.text || '').trim() || m._context === 'stage-three')).length;
    },
    pinGiftBridge(chatContainer) {
      const ms = Array.isArray(this.messages) ? this.messages : [];
      const last = [...ms].reverse().find(m => m && (m.type === 'user' || (m.type === 'reader' && m._context !== 'quest-line')));
      if (!last || last._context !== 'gift-bridge' || this.giftReplyOpen) return false;
      const panels = chatContainer.querySelectorAll('.talk-card');
      const panel = panels[panels.length - 1];
      const chips = chatContainer.querySelector('.gcb');
      if (!panel || !chips) return false;
      const box = chatContainer.closest('.chat-box-container');
      const scroller = [chatContainer, box].find(el => el && el.scrollHeight > el.clientHeight + 1) || chatContainer;
      chatContainer.style.paddingBottom = '';
      const view = scroller.getBoundingClientRect();
      const anchor = panel.querySelector('.tc-body') || panel;
      const over = chips.getBoundingClientRect().bottom - view.bottom + 8;
      const room = anchor.getBoundingClientRect().top - view.top - 12;
      scroller.scrollTop += Math.min(over, room);
      return true;
    },
    pinTalkCard(chatContainer) {
      const panels = chatContainer.querySelectorAll('.talk-card');
      const panel = panels[panels.length - 1];
      if (!panel) return false;
      // Nothing she has said since it, and no newer panel: it is the moment
      // (a quest line or the chips under it do not take the lead from it).
      const said = [...chatContainer.querySelectorAll('.message-box.user-message, .talk-card')];
      if (said[said.length - 1] !== panel) return false;
      // The Mystic has spoken since (the gift room's line after a write-back,
      // her next turn): that is the moment now, so the panel lets go.
      if (this.linesAfterLastTalkCard() > 0) return false;
      const id = panel.getAttribute('data-card-id') || String(panels.length);
      if (!this.pinnedTalkCards) this.pinnedTalkCards = new Set();
      if (this.pinnedTalkCards.has(id)) {
        // Held: as the words fill in, give back the room added under it, never
        // moving the scroll.
        const cur = parseFloat(chatContainer.style.paddingBottom) || 0;
        if (cur) {
          const boxEl = chatContainer.closest('.chat-box-container');
          const sc = [chatContainer, boxEl].find(el => el && el.scrollHeight > el.clientHeight + 1) || chatContainer;
          const want = Math.max(0, Math.ceil(sc.scrollTop + sc.clientHeight - (sc.scrollHeight - cur)));
          if (want < cur) chatContainer.style.paddingBottom = want ? `${want}px` : '';
        }
        return true;
      }
      // Her card in the gift room (gift card order, 2026-10-29): it lands
      // under the Mystic's lines. The line before it leads, and no room is
      // added under it: the room taken away again when the bridge came let
      // the opener and "Hold this question…" pop in above the card, after it.
      const ms = Array.isArray(this.messages) ? this.messages : [];
      const lastCard = [...ms].reverse().find(m => m && m._context === 'talk-card');
      if (talkCardLead(lastCard && lastCard.talk_card) === 'line') {
        chatContainer.style.paddingBottom = '';
        let line = panel.previousElementSibling;
        while (line && !line.matches('.message-box.reader-response')) line = line.previousElementSibling;
        const boxEl = chatContainer.closest('.chat-box-container');
        const sc = [chatContainer, boxEl].find(el => el && el.scrollHeight > el.clientHeight + 1) || chatContainer;
        const view = sc.getBoundingClientRect();
        // The line leads when it and the card's head fit together; else the card does.
        const fits = line && panel.getBoundingClientRect().top + 120 - line.getBoundingClientRect().top <= sc.clientHeight - 12;
        const lead = fits ? line : panel;
        sc.scrollTop = leadScrollTop({ scrollTop: sc.scrollTop, scrollHeight: sc.scrollHeight, clientHeight: sc.clientHeight,
          leadTop: lead.getBoundingClientRect().top, paneTop: view.top });
        clearTimeout(this.talkPinTimer);
        this.talkPinTimer = setTimeout(() => { this.pinnedTalkCards.add(id); }, 900);
        return true;
      }
      const HEADROOM = 12;
      const box = chatContainer.closest('.chat-box-container');
      chatContainer.style.paddingBottom = '';
      // Room under it, so its top can reach the pane's edge while the words
      // are still on their way.
      const scroller = box || chatContainer;
      const need = Math.max(0, scroller.clientHeight - panel.getBoundingClientRect().height - HEADROOM);
      if (need > 0) chatContainer.style.paddingBottom = `${need}px`;
      for (const el of [chatContainer, box]) {
        if (!el) continue;
        el.scrollTop += panel.getBoundingClientRect().top - el.getBoundingClientRect().top - HEADROOM;
      }
      // Lead once the layout has settled (the pane folds in ~0.5s); then hold.
      clearTimeout(this.talkPinTimer);
      this.talkPinTimer = setTimeout(() => { this.pinnedTalkCards.add(id); }, 900);
      return true;
    },
    scrollChatBox() {
      this.$nextTick(() => {
        const chatContainer = this.$refs.chat_container_ref;
        if (!chatContainer) return;
        const pin = () => {
          // The gift room, after their card is read (DESIGN-2 §38, N3): the
          // Mystic's bridge and the two end chips come up, as far as the card
          // and its phrase stay in view (a phone's pane cannot hold the whole
          // panel and the chips; the card leads, never scrolled away).
          if (this.pinGiftBridge(chatContainer)) return;
          // A pulled card is the newest thing in the chat: its panel leads,
          // its top 12px under the pane's edge, whatever its height. Once it
          // has led, the scroll does not move as its words fill in (2026-10-21
          // talk card design): the card and the phrase stay put.
          if (this.pinTalkCard(chatContainer)) return;
          // The gift room opens on the Mystic's first message: it leads, whole,
          // with the chips under it (2026-10-22 §5).
          // The reply card open in place (DESIGN-2 §39): Send in view, not the top.
          if (this.giftReplyOpen) { this.revealGiftReply(); return; }
          if (this.messages.length === 1 && this.messages[0] && this.messages[0]._context === 'gift-arrival') {
            chatContainer.style.paddingBottom = '';
            chatContainer.scrollTop = 0;
            const box0 = chatContainer.closest('.chat-box-container');
            if (box0) box0.scrollTop = 0;
            // A phone's short pane: bring both chips up, as long as the line's
            // first words stay in view.
            const line0 = chatContainer.querySelector('.message-box.reader-response');
            const chips0 = chatContainer.querySelector('.gcb');
            if (line0 && chips0) {
              const view = chatContainer.getBoundingClientRect();
              const over = chips0.getBoundingClientRect().bottom - view.bottom + 8;
              const room = line0.getBoundingClientRect().top - view.top - 4;
              if (over > 0 && room > 0) chatContainer.scrollTop = Math.min(over, room);
            }
            return;
          }
          // The grounding beat is centred on purpose; pinning to the bottom
          // scrolled it — and its ring — clean out of the pane for the
          // whole held pause (a blank pane where the Mystic was speaking).
          // The landing after the weave reads from the top too: the advice
          // card's caption led, and pinning down tucked it under the Finish
          // Reading control while the bar's empty bottom sat in view
          // (utils/chat-pin-edge).
          const edge = chatPinEdge({
            grounding: this.inGroundingPause || this.groundingArrivingNow,
            readingLanded: !!(this.readingActive && this.isReadingReady),
            postReading: this.postReadingMenuShowing,
            // "What landed?" and the Journal line follow a landed reading: bring them up.
            rateOpen: this.rateOpen || this.stageTwoTail,
            talking: !!(this.talkComposer && this.talkOver),
          });
          if (edge === 'last-message') {
            // Talk it through: her last line and the Mystic's answer lead.
            const scroller = chatContainer.closest('.chat-box-container') || chatContainer;
            const said = chatContainer.querySelectorAll('.message-box.reader-response:not(.static_suggestions), .message-box.user-message, .card-message');
            const mine = [...said].filter(el => el.classList.contains('user-message'));
            const lastSaid = said[said.length - 1];
            // Her line (or the card she just pulled) before the answer, when
            // both fit; else the answer.
            const before = said[said.length - 2];
            const lead = (before && (mine.includes(before) || before.classList.contains('card-message'))
              && lastSaid.getBoundingClientRect().bottom - before.getBoundingClientRect().top < scroller.clientHeight - 60)
              ? before : lastSaid;
            if (lead) {
              const HEADROOM = 12;
              // The talk's first question (2026-10-30): the advice card's
              // reminder and the question under it lead from the top. Nothing
              // is below the question yet, so give the chat the room to.
              if (lead.closest('.cr-talk-recap')) {
                chatContainer.style.paddingBottom = '';
                const boxEl = chatContainer.closest('.chat-box-container');
                const sc = [chatContainer, boxEl].find(el => el && el.scrollHeight > el.clientHeight + 1) || chatContainer;
                const span = lastSaid.getBoundingClientRect().bottom - lead.getBoundingClientRect().top;
                const need = Math.max(0, Math.ceil(sc.clientHeight - span - HEADROOM));
                if (need > 0) chatContainer.style.paddingBottom = `${need}px`;
              }
              for (const el of [chatContainer, chatContainer.closest('.chat-box-container')]) {
                if (!el) continue;
                el.scrollTop += lead.getBoundingClientRect().top - el.getBoundingClientRect().top - HEADROOM;
              }
            }
            return;
          }
          if (edge === 'top') {
            chatContainer.scrollTop = 0;
            const box = chatContainer.closest('.chat-box-container');
            if (box) box.scrollTop = 0;
            return;
          }
          const box = chatContainer.closest('.chat-box-container');
          if (edge === 'last-card') {
            const cards = chatContainer.querySelectorAll('.card-message');
            const last = cards[cards.length - 1];
            if (last) {
              // Put the last card's frame just under the floating header, on
              // whichever of the two boxes actually scrolls. There is rarely
              // enough below the last card to scroll it that high, so give
              // the chat the room: the tail of the card before it must not
              // hang cut off at the top.
              const HEADROOM = 44;
              const scroller = box || chatContainer;
              const need = Math.max(0, scroller.clientHeight - last.getBoundingClientRect().height - HEADROOM);
              chatContainer.style.paddingBottom = `${need}px`;
              for (const el of [chatContainer, box]) {
                if (!el) continue;
                el.scrollTop += last.getBoundingClientRect().top - el.getBoundingClientRect().top - HEADROOM;
              }
              // Rate / Share are the next step. In a short pane the card
              // alone fills it and the buttons sat under the fold, showing
              // only their top edge: give up some of the card instead.
              // Any chip row under the landed reading, the login button too (R3.10).
              const menu = chatContainer.querySelector('.message-box.static_suggestions');
              if (menu) {
                for (const el of [chatContainer, box]) {
                  if (!el) continue;
                  const over = menuOverflow(menu.getBoundingClientRect(), el.getBoundingClientRect());
                  if (over > 0) el.scrollTop += over;
                }
              }
              return;
            }
          }
          chatContainer.style.paddingBottom = '';
          chatContainer.scrollTop = chatContainer.scrollHeight;
          // The inner .chat-container can fit its content while the outer
          // .chat-box-container is the real scroller — pin both so the last
          // card message (while weaving) and the post-reading action buttons
          // (when the reading lands) are actually brought into view.
          if (box) box.scrollTop = box.scrollHeight;
          // Paced draws (2026-10-23): the newest reveal is the last thing in
          // the chat. In a pane shorter than its panel, its top (the pre-line
          // and the card) leads rather than its foot; earlier reveals stay
          // above, a scroll away.
          const saidLast = [...chatContainer.querySelectorAll('.message-box')].pop();
          const reveal = saidLast && saidLast.querySelector('.card-message');
          if (reveal) {
            for (const el of [chatContainer, box]) {
              if (!el) continue;
              const up = revealTopShortfall(reveal.getBoundingClientRect(), el.getBoundingClientRect());
              if (up > 0) el.scrollTop -= up;
            }
          }
        };
        pin();
        // Re-pin after late content lays out — the card image and the
        // Rate/Share buttons grow the box after the first paint.
        setTimeout(pin, 120);
        setTimeout(pin, 400);
      });
    },
    // When the held follow-up question surfaces (reading loaded), scroll the
    // chat to the bottom so the question is visible — once now, and again
    // after the reading-pane tween settles.
    scrollOnFollowUp() {
      this.scrollChatBox();
      setTimeout(() => this.scrollChatBox(), 320);
    },
    // ─── Exit Reading — one-tap return to the Doorman menu ──────────
    onExitReading() {
      this.$emit('exit-session');
    },
  }
};
</script>

<style scoped>
/* Round 2 (DESIGN-2 §40): what came before their own reading, dimmed. */
.cr-faded { opacity: 0.38; transition: opacity 0.4s ease; }
/* ── Reader chat surface tokens ───────────────────────────────────
   Not a new palette: these are the values the Doorman menu mode in
   this same file already paints with. The conversation was the one
   surface still on white, so it gets the same ground.

   Voice is carried by SHAPE, not by a pastel swap:
     Aida  — unboxed, lit type on the ground, one gold hairline
     Seeker— contained, a violet capsule
   ───────────────────────────────────────────────────────────────── */
.chat-container-panel {
  --cr-ground:      #0d0520;
  --cr-gold:        #f0d9a3;
  --cr-gold-soft:   rgba(240, 217, 163, 0.42);
  --cr-lavender:    #9b86c9;
  --cr-ink:         #ece3ff;  /* Aida's voice */
  --cr-ink-soft:    #ded0fb;  /* the seeker's */
  --cr-line:        rgba(155, 134, 201, 0.18);
  --cr-glass:       rgba(36, 26, 63, 0.72);
  --cr-serif:       'Cormorant Garamond', Georgia, serif;
  --cr-utility:     'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

/* Exit Reading button in the chat header. */
.exit-reading-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
/* A 44px touch target past the 26px pill (e2e round 2). */
.exit-reading-trigger::after { content: ''; position: absolute; inset: -10px -2px; }

/* Emerald gem inside quick-action button labels — substituted for the
 * BE's ✦ sentinel so prices like "New Reading (50 ✦)" render with the
 * same gem the top-left balance uses. */
.qa-emerald {
  color: #50C878;
  margin: 0 2px;
  font-weight: 900;
}

/* General chat container styles */
.chat-container-panel {
  position: relative;
  top: 0;
  right: 0;
  height: 100%; /* Ensure it occupies full height */
  background-color: var(--cr-ground);
  box-shadow: none;
  padding: 10px 0 5px 0;
  width: 100%;
  border-radius: 8px;
  overflow: auto;
  box-sizing: border-box;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
}
.chat-container-panel.no-menu {
  padding-bottom: 0 !important;
}


.chat-container {
  flex: 1; /* Allow it to grow and fill available space */
  overflow-y: auto;
  /* Reserve the scrollbar gutter on BOTH edges so centered content (the
     post-reading Rate/Share bar) stays centered in the window instead of
     shifting left by the right-hand scrollbar's width. */
  scrollbar-gutter: stable both-edges;
  display: flex;
  flex-direction: column;
  justify-content: flex-start; /* Align messages at the top */

  padding: 44px 12px 14px; /* Headroom for the floating header row */
  background: radial-gradient(125% 95% at 50% 0%, #1c1035 0%, #120827 54%, #0d0520 100%);
  border-radius: 14px;
  border: 1px solid var(--cr-line);
}
/* Empty chat (start menu or preparing) sits on a mystical dark backdrop rather
   than a bare white void — the menu cards are already dark, so they cohere. The
   white surface returns the moment a real conversation begins. */
.chat-container.cr-empty {
  background: radial-gradient(120% 90% at 50% 18%, #241a3f 0%, #150b28 55%, #0d0520 100%);
  border-color: transparent;
}

/* ── Doorman menu surface ─────────────────────────────────────────
   In the DOORMAN entry state the chat window IS the reader's menu: a
   greeting + the mode cards. Routing that through the normal chat gave a
   white .chat-container framed by the grey .chat-container-panel, so the
   dark cards floated on a bare white slab (ugly at polish). cr-empty can't
   catch it — the greeting counts as a message, so !messages.length is false.

   Key off showMenuCards (=== DOORMAN) instead: present the whole surface as
   a themed dark menu, and restyle the greeting from a peach chat bubble into
   a centered menu header. The white chat chrome returns on its own the
   moment a card is tapped and conversation_state leaves DOORMAN. */
.chat-container-panel.cr-menu-surface {
  /* Dark base so the panel's top padding + reserved scrollbar gutters read as
     the menu backdrop, not a grey/white frame. */
  background-color: #0d0520;
  box-shadow: none;
}
.chat-container.cr-menu {
  background: radial-gradient(120% 90% at 50% 12%, #241a3f 0%, #150b28 55%, #0d0520 100%);
  border-color: transparent;
  /* Greeting sits at the top; the card grid flexes to fill the rest of the
     section (below) so the cards occupy the space instead of floating centered
     in a dark void. Bottom padding keeps the lowest card clear of the bottom
     nav, which overlaps the lower ~56px of this pane. */
  justify-content: flex-start;
  /* Balanced margins: ~10px top (reads as ~20px from the divider once the
     panel's own 10px is added) and 86px bottom, which clears the 67px bottom
     nav that overlaps this pane and leaves a matching ~19px gap above it. */
  padding: 10px 12px 86px;
  /* Drop the scroll + reserved gutter in menu mode. The gutter is what threw the
     cards off-center: when the menu scrolls, Safari's partial scrollbar-gutter
     support insets only the RIGHT edge, shifting the cards left off the avatar's
     axis. A Doorman menu is short and fits, so removing the scroll keeps the
     cards centered on the full pane width. Only while the cards show
     (cr-menu-still): anything else in this state (the gift room) scrolls. */
}
.chat-container.cr-menu.cr-menu-still {
  overflow: hidden;
  scrollbar-gutter: auto;
}
/* The outer scroller reserves the same both-edges gutter — neutralize it in menu
   mode too, so neither scroll section can inset/shift the centered cards. */
.chat-container-panel.cr-menu-surface.cr-menu-still .chat-box-container {
  overflow: hidden;
  scrollbar-gutter: auto;
}
/* Fill the section: the grid grows to take all height under the greeting, and
   its rows share that height equally (grid-auto-rows: 1fr) so the cards stretch
   to fill instead of sitting at a fixed 104px. */
.chat-container.cr-menu .qa-cards {
  flex: 1 1 auto;
  align-content: stretch;
  grid-auto-rows: 1fr;
  /* No vertical auto-margin — the container padding owns the top/bottom gaps so
     they stay balanced. (Greeting-to-cards spacing lives on the greeting's
     margin-bottom, so it doesn't inflate the no-greeting top gap.) */
  margin: 0 auto;
  max-width: 560px;
}
/* Release the fixed card height so the 1fr grid rows stretch them; keep a floor
   so a single-item menu still reads as a card, not a slab. */
.chat-container.cr-menu .qa-card { height: auto; min-height: 116px; }
/* Greeting reads as a menu header, not a chat bubble. */
.chat-container.cr-menu .message-box.reader-response {
  background: transparent;
  box-shadow: none;
  max-width: 100%;
  align-self: center;
  text-align: center;
  /* margin-bottom carries the greeting→cards gap so the grid needs no top margin. */
  margin: 10px auto 14px;
  padding: 6px 16px 0;
}
.chat-container.cr-menu .message-box.reader-response .text-message {
  color: #efe6ff;
  font-family: 'Cinzel', serif;
  font-size: 17px;
  line-height: 1.45;
  text-align: center;
}
/* Themed loading screen shown while the chat is empty/preparing — fills the
   white container edge-to-edge (cancels its 10px padding) so there's no bare
   white void on the dark app before the first message streams in. */
.cr-preparing {
  flex: 1;
  align-self: stretch;
  min-height: 240px;
  /* Cancel .chat-container's padding so the themed screen fills edge to edge. */
  margin: -44px -12px -14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  border-radius: 12px;
  background: radial-gradient(120% 90% at 50% 22%, #241a3f 0%, #150b28 58%, #0d0520 100%);
}
.cr-preparing-orb {
  font-size: 26px;
  color: #e0b64a;
}
.cr-preparing-text {
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.1rem;
  letter-spacing: 0.04em;
  color: #d9c8f5;
}
@media (prefers-reduced-motion: reduce) {
  .cr-preparing-orb { animation: none; }
}
.chat-box-container {
  flex: 1; /* Make it take all available space */
  display: flex;
  flex-direction: column;
  justify-content: flex-start; /* Start messages at the top */
  overflow-y: auto; /* Make this the scrolling container */
  scrollbar-gutter: stable both-edges;
  height: 100%;
  min-height: 0; /* Allow flex shrinking for proper layout */
}
.follow-up-container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  padding-bottom: calc(6px + env(safe-area-inset-bottom, 0px));
  background: var(--cr-ground);
  border-top: 1px solid var(--cr-line);
  z-index: 10;
  box-sizing: border-box;
  flex-shrink: 0;
}
.reader-name-tab {
  position: absolute;
  top: 13px; /* Optically centered against the header pills */
  left: 50%; /* Center horizontally */
  transform: translateX(-50%);
  /* It names where you are; it is not a control. Set as a quiet
     small-caps label rather than a framed, shadowed pill. */
  background: none;
  border: none;
  box-shadow: none;
  color: var(--cr-lavender);
  font-family: var(--cr-utility);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 0 20px;
  z-index: 10; /* Ensure it stays above other elements */
  text-align: center;
  white-space: nowrap; /* Prevent text from wrapping */
}

/* Reading History link in the chat header. Sits to the right of
 * the reader-name-tab pill. Keeps the visual weight low so it
 * doesn't compete with the primary nav buttons. */
.chat-top-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 11;
}
/* Care Package intent: who this reading is for. Top centre, one turn only.
   It has to be seen in that short window, so it is a full-strength control:
   bright labels, a gold rim, and the chosen side filled. */
/* Sticky while it is offered: the grounding beat and the opening question can
   push it out of the ~270px phone scroller during the only window it exists. */
.cr-intent { display: flex; flex-direction: column; align-items: center; gap: 10px; margin: 0; padding: 8px 12px 10px;
  position: relative; z-index: 12; flex: none; }
/* Above 480px the header row (Exit Reading · In session with … · history)
   floats over the top of the chat with a dark scrim at z-index 20. The toggle
   sat underneath it: dimmed by the scrim, the session label across its rim.
   Start it below that row. (At 480px and under the row is hidden.) */
@media (min-width: 481px) {
  .cr-intent { padding-top: 52px; }
  /* The same row carries the session label ("Pick 4 Cards from above."),
     plain text with no pill behind it. Over a scrolled conversation it was
     drawn across the top line of a message (picker open, desktop). While the
     label shows, the conversation's window starts below the row instead of
     beneath it: the 44px headroom becomes margin, so nothing scrolls under
     the label. The same content height. */
  .chat-container.cr-headed:not(.cr-menu):not(.cr-empty):not(.cr-grounding) {
    margin-top: 38px;
    padding-top: 6px;
  }
  .cr-intent ~ .chat-container.cr-headed:not(.cr-menu):not(.cr-empty):not(.cr-grounding) {
    margin-top: 0;
    padding-top: 44px;
  }
}
.cr-intent-toggle { display: inline-flex; gap: 4px; padding: 4px; border-radius: 999px;
  border: 1px solid rgba(240,217,163,0.7); background: rgba(28,16,53,0.92);
  box-shadow: 0 0 0 4px rgba(240,217,163,0.06), 0 8px 24px -10px rgba(0,0,0,0.8); }
.cr-intent-toggle button { display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px 20px; min-height: 44px; border: 1px solid transparent; white-space: nowrap;
  border-radius: 999px; background: transparent; color: #e6dcff; font-family: var(--cr-utility);
  font-size: 15px; font-weight: 600; letter-spacing: 0.03em; cursor: pointer;
  transition: background 0.18s, color 0.18s, border-color 0.18s; }
.cr-intent-toggle button i { font-style: normal; font-size: 15px; line-height: 1; }
.cr-intent-toggle button:hover:not(.on) { background: rgba(123,75,176,0.22); color: #fff; }
.cr-intent-toggle button:active { background: rgba(123,75,176,0.35); }
.cr-intent-toggle button.on { background: linear-gradient(180deg, #8a57c4, #5a2f88); color: #FFDFA3;
  border-color: rgba(240,217,163,0.55); box-shadow: 0 0 14px -4px rgba(240,217,163,0.55); }
.cr-intent-toggle button:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 2px; }
.cr-intent-pencil { font-size: 9px; opacity: 0.8; margin-left: 2px; }
.cr-intent-done { flex: none; min-width: 44px; min-height: 44px; padding: 0 14px; border-radius: 999px; border: 1px solid rgba(240,217,163,0.55);
  background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #FFDFA3; font-family: var(--cr-utility); font-size: 12px; letter-spacing: 0.06em;
  cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 6px; }
.cr-intent-done:disabled { opacity: 0.35; cursor: default; }
.cr-intent-who { position: relative; width: min(420px, 100%); }
.cr-intent-box { display: flex; align-items: center; gap: 8px; border: 1px solid rgba(195,169,255,0.35); border-radius: 12px;
  padding: 4px 4px 4px 12px; background: #1c1035; }
.cr-intent-box small { font-family: var(--cr-utility); font-size: 10.5px; letter-spacing: 0.12em; text-transform: uppercase;
  color: #8a78ad; flex: none; }
.cr-intent-box input { flex: 1; min-width: 0; background: transparent; border: none; outline: none; color: var(--cr-ink, #e9dcff);
  font-family: 'Cormorant Garamond', Georgia, serif; font-size: 17px; }
.cr-intent-box input::placeholder { color: #6f5f94; }
.cr-intent-list { position: absolute; left: 0; right: 0; top: calc(100% + 6px); margin: 0; padding: 0; list-style: none; z-index: 20;
  border: 1px solid rgba(195,169,255,0.25); border-radius: 12px; background: #1c1035; overflow: hidden auto; max-height: 40dvh;
  box-shadow: 0 18px 40px -16px #000; }
.cr-intent-list li { display: flex; align-items: center; gap: 10px; padding: 8px 12px; min-height: 44px; font-size: 15px; color: var(--cr-ink, #e9dcff);
  border-top: 1px solid rgba(195,169,255,0.16); cursor: pointer; }
.cr-intent-list li:first-child { border-top: none; }
.cr-intent-list li:hover { background: rgba(123,75,176,0.25); }
.cr-intent-list li i, .cr-intent-list li img { width: 22px; height: 22px; border-radius: 50%; object-fit: cover; flex: none;
  background: linear-gradient(135deg, #6a3fa0, #f0d9a3); display: inline-block; }
.cr-intent-list li small { margin-left: auto; font-size: 10.5px; color: #8a78ad; }
.cr-stalled { margin-top: 14px; display: flex; flex-direction: column; align-items: center; gap: 8px; }
.cr-stalled p { margin: 0; font-size: 14px; color: #9b86c9; }
.cr-stalled-btn { min-height: 44px; padding: 10px 18px; border-radius: 999px; border: 1px solid rgba(240,217,163,0.55);
  background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #FFDFA3; font-family: var(--cr-utility); font-size: 12px;
  letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer; }
.cr-stalled-inline { font-size: 12px; opacity: 0.8; }
/* Phones: the chat leads with the last card after Summary (chat-pin-edge), and
   the chip row (Rate / Gift, or the login button for a signed-out reading)
   sits in the flow right under it. It used to be stuck to the pane's bottom
   edge, which put "Log in to rate & share" over the advice card's line on a
   phone (R3.10, dev walk 2026-10-15). Below the fold is handled by the pin
   instead: scrollChatBox gives up some of the card's top so the row fits. */
@media (max-width: 699px) {
  .chat-container > .message-box.static_suggestions {
    margin-bottom: 0;
    padding-top: 10px; padding-bottom: 6px;
  }
}
/* Touch feedback: the first tap used to give nothing back (hover-only styling). */
.static_suggestions button:active, .qa-card:active, .cr-choice-link:active { transform: scale(0.98); filter: brightness(1.1); }
/* No icons-only mode on phones: testers never found "for someone" behind a
   bare glyph. "for me · for someone" is 23 characters and fits at 390px. */
@media (max-width: 560px) {
  /* Two equal halves across the width, so neither label is ever clipped. */
  .cr-intent-toggle { display: flex; width: min(360px, 100%); box-sizing: border-box; }
  .cr-intent-toggle button { flex: 1 1 0; min-width: 0; padding: 10px 8px; font-size: 14.5px; gap: 6px; }
  .cr-intent-toggle button span { overflow: hidden; text-overflow: ellipsis; }
}
.finish-reading-control {
  display: inline-flex;
  align-items: center;
  padding: 7px 16px;
  background: linear-gradient(180deg, #f5e4ba, #dcbf7e);
  border: none;
  border-radius: 999px;
  color: #2a1845;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-family: var(--cr-utility);
  cursor: pointer;
  box-shadow: none;
}
.finish-reading-control { position: relative; }
/* A 44px touch target past the 26px pill (e2e round 2). */
.finish-reading-control::after { content: ''; position: absolute; inset: -10px -2px; }
.finish-reading-control:hover { filter: brightness(1.08); }
.reading-history-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: var(--cr-glass);
  border: 1px solid rgba(155, 134, 201, 0.32);
  border-radius: 999px;
  color: #d9cbf5;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  z-index: 11;
}
.reading-history-link:hover { background: rgba(102, 51, 153, 0.4); color: var(--cr-gold); }
.reading-history-link i { font-size: 12px; }
@media (max-width: 600px) {
  .reading-history-link span { display: none; }
  .reading-history-link { padding: 6px 8px; }
}

/* Append / Integrate under Aida's journal reflections. */
.cr-fold { display: flex; gap: 14px; margin-top: 7px; }
.cr-outcome { display: flex; flex-direction: column; gap: 8px; margin-top: 10px;
  padding-top: 10px; border-top: 1px solid rgba(155,134,201,0.2); }
.cr-outcome-desc { font-size: 12.5px; line-height: 1.55; color: #e9dcff;
  font-style: normal; background: rgba(0,0,0,0.25); border-radius: 10px;
  padding: 10px 12px; margin: 0 0 10px; max-height: 190px; overflow-y: auto; }
.cr-outcome-note { font-size: 12px; color: #9b86c9; font-style: normal; }
.cr-outcome-btn { align-self: flex-start; background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #fff; border: none; border-radius: 999px; padding: 8px 18px; font-size: 12.5px;
  font-weight: 700; font-family: inherit; font-style: normal; cursor: pointer; }
.cr-outcome-btn:hover { filter: brightness(1.12); }
.cr-append-btn { background: none; border: none; color: #C3A9FF; font-family: inherit;
  font-size: 11.5px; cursor: pointer; padding: 0; opacity: 0.8; }
.cr-append-btn:hover { opacity: 1; color: #f0d9a3; text-decoration: underline; }

/* General reader-response message box (dynamic width for normal messages) */
.message-box.reader-response {
  display: flex; /* Flex for centering widget content */
  justify-content: center;
  max-width: 88%; /* Limit the maximum width of normal messages */
  padding: 1px 0 1px 20px; /* Room for the gold rule */
  margin: 18px 0; /* Space between messages */
  border-radius: 0;
  background-color: transparent;
  box-shadow: none;
  word-wrap: break-word; /* Ensure long words wrap properly */
  overflow-wrap: break-word; /* Additional safeguard for word wrapping */
  white-space: pre-wrap; /* Preserve line breaks while allowing wrapping */
  overflow: visible; /* Ensure all content is fully displayed */
  box-sizing: border-box; /* Include padding in width calculations */
}
/* Widget Section START */
.birthdate-widget {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 15px;
  border-radius: 12px;
  border: 1px solid rgba(155, 134, 201, 0.32);
  background-color: rgba(36, 26, 63, 0.72);
}

.birthdate-input {
  padding: 8px 12px;
  font-size: 16px;
  border-radius: 8px;
  color: var(--cr-ink);
  background-color: rgba(13, 5, 32, 0.6);
  border: 1px solid rgba(155, 134, 201, 0.32);
}
button {
  background-color: #663399;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

button:hover:not(:disabled) {
  background-color: #5a2d85;
}
/* Widget Section END */

.chat-card-selection{
  width:100%
}
/* Thinking message */
.thinking-message {
  position: absolute; /* Place it within chat-container */
  bottom: 10px; /* Offset from the bottom of the container */
  left: 10px; /* Offset from the left of the container */
  background-color: rgba(0, 0, 0, 0.75); /* Semi-transparent black background */
  color: white;
  padding: 5px 10px; /* Padding for better readability */
  border-radius: 5px; /* Rounded corners */
  font-size: 14px; /* Font size adjustment */
  font-style: italic; /* Add an italic style for effect */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2); /* Subtle shadow */
  z-index: 10; /* Ensure it stays on top of other elements */
}
/* Quick actions */
.quick-actions-panel {
  flex-shrink: 0; /* Prevent shrinking */
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 10px 0;
}
.quick-actions-panel button {
  background-color: #663399; /* Purple */
  color: white;
  border: none;
  padding: 10px 15px;
  text-align: center;
  font-size: 14px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.3s ease, transform 0.2s ease;
}
.quick-actions-panel button:hover {
  background-color: #5a2d85; /* Darker purple */
  transform: scale(1.05);
}

.bottom-suggestions {
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 8px 10px 4px;
}
/* Magic / Spoken mode bar above the input (mirrors the Studio composer). */
.chat-mode-bar {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 8px 10px 2px;
}
.chat-mode-select {
  padding: 5px 9px; border-radius: 8px; border: 1px solid rgba(155, 134, 201, 0.45);
  background: rgba(20, 8, 40, 0.55); color: #e9dcff; font-size: 12.5px;
  font-family: inherit; cursor: pointer;
}
.chat-mode-length {
  display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #9b86c9;
}
.chat-mode-length input {
  width: 52px; padding: 4px 6px; border-radius: 8px; border: 1px solid rgba(155, 134, 201, 0.45);
  background: rgba(20, 8, 40, 0.55); color: #e9dcff; font-size: 12.5px; font-family: inherit;
}
.chat-mode-length-unit { color: #7c6ba0; }

/* Name-picker tier filter + option cards (same concept as the Studio). */
.cr-tier-filter { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 10px 2px; }
.cr-tier-chip { background: rgba(20,8,40,0.5); border: 1px solid rgba(155,134,201,0.35); color: #9b86c9;
  border-radius: 999px; padding: 5px 11px; font-size: 12px; cursor: pointer; font-family: inherit; }
.cr-tier-chip.on { background: rgba(102,51,153,0.5); color: #f0d9a3; border-color: rgba(240,217,163,0.4); }
.cr-name-options { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 8px;
  width: 100%; margin: 8px 0; }
.cr-name-topup { width: 100%; margin-top: 6px; background: transparent;
  border: 1px solid rgba(240,217,163,0.4); color: #f0d9a3; border-radius: 999px;
  padding: 8px 16px; font-size: 12.5px; font-family: inherit; cursor: pointer; }
.cr-name-topup:hover { background: rgba(240,217,163,0.1); }
.cr-name-cost.short { color: #f0a3a3; }
.cr-name-card.state-yours { opacity: 0.75; border-color: rgba(240,217,163,0.45); }
.cr-name-card.state-short { opacity: 0.6; }
.cr-name-card { text-align: left; background: rgba(20,8,40,0.5); border: 1px solid rgba(155,134,201,0.3);
  border-radius: 12px; padding: 10px 12px; cursor: pointer; font-family: inherit; color: #e9dcff; }
.cr-name-card:hover:not(:disabled) { border-color: rgba(240,217,163,0.5); }
.cr-name-card.taken { opacity: 0.5; cursor: default; }
.cr-name-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.cr-name-name { font-family: 'Cinzel', serif; font-size: 15px; color: #f5e8c0; }
.cr-name-tier { font-size: 9.5px; letter-spacing: 0.06em; text-transform: uppercase; color: #9b86c9; }
.cr-name-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 5px; }
.cr-name-slug { font-size: 11px; color: #8a78ad; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cr-name-cost { font-size: 12px; color: #f0d9a3; font-weight: 700; flex-shrink: 0; }
.cr-name-cost.free { color: #6fcf97; }
.cr-name-status { display: block; font-size: 10px; color: #f0a3a3; margin-top: 3px; }
.bottom-suggestion-chip {
  background: rgba(102, 51, 153, 0.28);
  border: 1px solid rgba(155, 134, 201, 0.45);
  color: #e9dcff; border-radius: 999px; padding: 5px 12px;
  font-size: 12.5px; cursor: pointer; font-family: inherit;
}
.bottom-suggestion-chip:hover { background: rgba(102, 51, 153, 0.5); color: #FFDFA3; }

.follow-up-container textarea {
  flex: 1; /* Allow the input to take up remaining space */
  /* No resize grabber: it breaks the capsule's corner, and the field
     already grows to max-height then scrolls. */
  resize: none;
  border: 1px solid var(--cr-line);
  border-radius: 21px;
  padding: 9px 17px;
  box-sizing: border-box;
  font-size: 17px;
  font-family: var(--cr-serif);
  color: var(--cr-ink);
  background-color: rgba(36, 26, 63, 0.55);
  min-height: 42px; /* Small initial height */
  max-height: 150px; /* Prevent the text area from growing excessively */
  overflow-y: auto; /* Enable scrolling when exceeding max height */
  line-height: 1.5; /* Consistent line height for text */
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.follow-up-container textarea::placeholder {
  color: var(--cr-lavender);
  opacity: 0.75;
}

/* Talk it through: the turns left beside the reply box. Small and calm;
   gold on the last one. */
.talk-left {
  flex: 0 0 auto;
  align-self: center;
  font-size: 11.5px;
  letter-spacing: 0.04em;
  color: var(--cr-lavender);
  opacity: 0.7;
  white-space: nowrap;
  font-family: var(--cr-serif);
}
.talk-left.is-last {
  color: #e8c77a;
  opacity: 1;
}

.follow-up-container textarea:focus {
  outline: none;
  background-color: rgba(36, 26, 63, 0.85);
  border-color: rgba(240, 217, 163, 0.45);
}

.follow-up-container > button {
  flex-shrink: 0; /* Prevent the button from shrinking */
  width: 42px;
  height: 42px;
  padding: 0;
  background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: var(--cr-gold);
  border: 1px solid rgba(240, 217, 163, 0.32);
  border-radius: 50%;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
}

.follow-up-container > button:hover {
  background: linear-gradient(180deg, #8a5bc4, #5a2f87);
  border-color: rgba(240, 217, 163, 0.7);
  box-shadow: 0 0 0 4px rgba(240, 217, 163, 0.09);
}

.follow-up-container > button i {
  font-size: 16px;
}

/* Message turns. The base box paints nothing — each voice declares
   its own shape, which is what tells you who is speaking. */
.message-box {
  background-color: transparent;
  border-radius: 0;
  padding: 0;
  margin: 20px 24px 20px 0;
  box-sizing: border-box;
  word-wrap: break-word;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
  box-shadow: none;
  max-width: 75%;
}

.message-box p {
  color: var(--cr-ink);
  margin: 0;
  font-family: var(--cr-serif);
  font-size: 19px;
  line-height: 1.62;
  white-space: pre-wrap;
}

/* ── Aida: unboxed ────────────────────────────────────────────────
   She is not a peer posting a message. Her words sit on the ground,
   anchored by a single gold rule that is brightest where the turn
   begins and falls away as it runs — so a long reading still reads
   as one utterance without a container drawn around it. */
.reader-response {
  position: relative;
  background: transparent;
  align-self: flex-start;
  max-width: 88%;
  padding: 1px 0 1px 20px;
}
.reader-response:has(.text-message)::before {
  content: '';
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 2px;
  border-radius: 2px;
  background: linear-gradient(180deg,
    var(--cr-gold) 0%, var(--cr-gold-soft) 38%, rgba(240, 217, 163, 0.05) 100%);
}
/* In the Doorman menu the greeting is a centered header, not a turn. */
.chat-container.cr-menu .reader-response::before { display: none; }

/* ── The seeker: contained ───────────────────────────────────────
   A capsule, squared off at the corner it came from. */
.user-message {
  align-self: flex-end;
  margin: 20px 0 20px 24px;
  max-width: 74%;
  padding: 11px 17px;
  background: linear-gradient(180deg, rgba(118, 71, 178, 0.38), rgba(70, 37, 116, 0.5));
  border: 1px solid rgba(155, 134, 201, 0.32);
  border-radius: 18px 18px 6px 18px;
}
.user-message p {
  color: var(--cr-ink-soft);
  font-size: 16.5px;
  line-height: 1.52;
}
/* A drawn card is pushed as a 'user' turn but is a card, not a reply —
   it brings its own frame, so the capsule steps out of its way. */
.user-message:has(.card-message) {
  max-width: 100%;
  width: 100%;
  padding: 0;
  background: none;
  border: none;
}
/* Image message container with card styling */
.card-message {
  display: flex;
  flex-direction: column; /* Stack elements vertically */
  align-items: center;
  background: linear-gradient(180deg, rgba(38, 27, 66, 0.9), rgba(14, 6, 34, 0.94));
  border: 1px solid rgba(240, 217, 163, 0.22);
  border-radius: 16px;
  padding: 16px;
  width: 95%;
  max-width: 95%; /* Prevent card from exceeding container width */
  margin: 10px auto; /* Centers the card */
  box-shadow: 0 20px 46px -24px rgba(0, 0, 0, 0.95);
  gap: 14px; /* Spacing between title and content */
}

.card-title {
  grid-column: 1 / -1; /* Span the entire grid width */
  text-align: center; /* Center the text */
  margin: 0; /* Minimal margin for spacing */
  width: 100%;
  /* The announcement line — which card landed where. It sets up the
     reveal, so it sits above it as a caption, not as a chip. */
  font-family: 'Cinzel', serif;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  line-height: 1.5;
  color: var(--cr-lavender);
  padding: 0 0 2px;
  border: none;
  background: none;
  box-shadow: none;
}

.card-content {
  display: flex; /* Align image and description side-by-side */
  flex-direction: row; /* Horizontal layout */
  align-items: center; /* Vertical align to center */
  gap: 15px; /* Spacing between image and text */
  width: 100%; /* Ensure full width */
  flex-wrap: nowrap; /* Prevent wrapping of content */
}

.card-image {
  height: 180px;
  border-radius: 10px;
  border: 1px solid rgba(240, 217, 163, 0.3); /* The card's own edge */
  object-fit: cover; /* Maintain aspect ratio while filling */
  box-shadow: 0 10px 26px -10px rgba(0, 0, 0, 0.85);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.card-image:hover {
  transform: translateY(-3px); /* Lifts off the table */
  box-shadow: 0 16px 34px -12px rgba(0, 0, 0, 0.9),
              0 0 22px -6px rgba(240, 217, 163, 0.3);
}

.card-text-container {
  flex: 1; /* Take up remaining space */
  display: flex; /* Align text inside */
  justify-content: center; /* Align text to the left */
  align-items: center; /* Vertical align to center */
  overflow: hidden; /* Prevent overflow issues */
  text-align: center;
  width: 100%;

}
.card-caption {
  white-space: normal; /* Prevents text wrapping */
  /* The reveal itself. This is the beat the seeker is waiting on, so
     it gets the display face and the gold — and no box, which is what
     was competing with the card image beside it. */
  font-family: 'Cinzel', serif;
  font-size: 1.45em;
  overflow: hidden; /* Hides overflow text */
  font-weight: 600; /* Bold for prominence */
  color: var(--cr-gold) !important;
  margin: 0; /* Increased spacing for better balance */
  text-align: center; /* Center-align for symmetry */
  text-transform: capitalize; /* Clean, consistent casing */
  letter-spacing: 0.02em; /* Subtle letter spacing */
  line-height: 1.34; /* Adjusted for larger font size */
  background: none;
  padding: 8px 6px;
  border: none;
  box-shadow: none;
}


.card-image.reversed-card {
  transform: rotate(180deg);
}
/* The words arrive softly, in the slot the reading line held. */
.card-message .card-caption { animation: reveal-words-in 0.32s ease-out both; }
/* While the headline is on its way: the same slot, a quiet line and three
   gold dots breathing in turn. */
.reveal-reading {
  flex-direction: column;
  gap: 10px;
  min-height: 3.2em;
  padding: 8px 6px;
  animation: reveal-words-in 0.32s ease-out both;
}
.reveal-reading-line {
  font-family: var(--cr-serif);
  font-style: italic;
  font-size: 1.05em;
  line-height: 1.4;
  color: var(--cr-lavender);
}
.reveal-dots { display: inline-flex; gap: 7px; }
.reveal-dots i {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--cr-gold);
  opacity: 0.25;
  animation: reveal-dot 1.4s ease-in-out infinite;
}
.reveal-dots i:nth-child(2) { animation-delay: 0.2s; }
.reveal-dots i:nth-child(3) { animation-delay: 0.4s; }
@keyframes reveal-dot {
  0%, 100% { opacity: 0.25; transform: scale(0.85); }
  45% { opacity: 0.95; transform: scale(1); box-shadow: 0 0 8px rgba(240, 217, 163, 0.55); }
}
@keyframes reveal-words-in { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .card-message .card-caption, .reveal-reading { animation: none; }
  .reveal-dots i { animation: none; opacity: 0.7; }
}
/* Talk it through (2026-10-30): the advice card steps back to a one-line
   reminder (thumbnail, the "you selected" line, the headline at reading
   size), so the Mystic's question under it is the focus. */
.cr-talk-recap .card-message {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 12px;
  row-gap: 2px;
  margin: 4px auto 6px;
  padding: 8px 12px;
  border-radius: 12px;
  box-shadow: none;
}
.cr-talk-recap .card-content { display: contents; }
.cr-talk-recap .card-title {
  grid-column: 2;
  grid-row: 1;
  text-align: left;
  font-family: var(--cr-utility);
  font-size: 12.5px;
  letter-spacing: 0.01em;
  text-transform: none;
  line-height: 1.35;
  padding: 0;
}
.cr-talk-recap .card-image {
  grid-column: 1;
  grid-row: 1 / span 2;
  height: 46px;
  width: auto;
  max-height: 46px;
  border-radius: 4px;
  box-shadow: none;
}
.cr-talk-recap .card-text-container {
  grid-column: 2;
  grid-row: 2;
  justify-content: flex-start;
  text-align: left;
}
.cr-talk-recap .card-caption {
  font-family: var(--cr-serif);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.3;
  text-align: left;
  padding: 0;
  animation: none;
}
/* .card-image:hover is (0,2,0) and would REPLACE the rotation — with
   `transition: transform` that animated a reversed card back upright on hover.
   Compose the lift into the rotated frame instead. */
.card-image.reversed-card:hover {
  transform: rotate(180deg) translateY(3px);
}

.quick-actions-trigger button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px; /* Spacing between icon and text */
  width: 42px;
  height: 42px;
  padding: 0;
  background: transparent;
  color: var(--cr-lavender);
  border: 1px solid rgba(155, 134, 201, 0.32);
  border-radius: 50%;
  font-size: 15px;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.quick-actions-trigger button:hover {
  background: rgba(102, 51, 153, 0.35);
  border-color: rgba(240, 217, 163, 0.45);
  color: var(--cr-gold);
}

.quick-actions-trigger .loading-spinner {
  display: flex; /* Align spinner and text */
  align-items: center;
  justify-content: center;
  background-color: #5a2d85; /* Purple background */
  color: white;
  border-radius: 50%;
  padding: 8px 12px;
  font-size: 14px; /* Adjust for better readability */
  width: auto; /* Allow button to grow with text */
  height: auto; /* Dynamically adjust height based on content */
  border: none;
  cursor: not-allowed;
  transition: background-color 0.3s ease;
  gap: 8px; /* Add spacing between spinner and text */
}
.quick-actions-trigger .loading-spinner i {
  font-size: 16px;
}
.quick-actions-dropdown {
  position: absolute;
  bottom: 10px;
  left: 10px; /* Align with the quick actions trigger */
  background-color: #1c1030;
  border: 1px solid rgba(195, 169, 255, 0.32);
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 16px 38px -10px rgba(0, 0, 0, 0.8);
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.quick-actions-dropdown button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 11px;
  text-align: left;
  background-color: transparent;
  color: #ece7f6;
  border: none;
  padding: 10px 14px;
  border-radius: 8px;
  font-family: var(--cr-utility);
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.quick-actions-dropdown .qa-action-icon {
  flex: none;
  width: 16px;
  text-align: center;
  font-size: 13px;
  color: #c3a9ff;
}
.quick-actions-dropdown button:hover {
  background-color: rgba(102, 51, 153, 0.4);
}
.quick-actions-dropdown button:hover .qa-action-icon { color: var(--cr-gold); }
.reader-nav-container {
  position: absolute; /* Ensures it stays at the top of the container */
  top: 0; /* Aligns with the top of the parent container */
  left: 0;
  width: 100%; /* Spans the entire width of the parent */
  display: flex; /* Flexbox for alignment */
  justify-content: space-between; /* Buttons on far left and right, tab centered */
  align-items: center; /* Vertical alignment */
  /* Scrim: the pills are glass now, so the conversation must fade out
     beneath them rather than collide with them as it scrolls. */
  background: linear-gradient(180deg, #0d0520 0%, rgba(13, 5, 32, 0.94) 44%, rgba(13, 5, 32, 0) 100%);
  pointer-events: none; /* Let the scroller beneath keep the drag */
  margin: 0; /* Removes any default margin */
  padding: 6px 0 26px; /* Ensures no padding is added */
  z-index: 20; /* Ensures it appears above other elements */
  box-sizing: border-box; /* Consistent sizing */
}

/* Reader Navigation Buttons */
/* One glass family for both edges. These were red / purple / green —
   three stoplight colors for what is really the same kind of control,
   which is most of why the header read as dated. Direction is carried
   by position and by the gold tint on the advancing action. */
.reader-nav-container > * { pointer-events: auto; }
.reader-nav-button {
  top: 0;
  background: var(--cr-glass);
  color: #d9cbf5;
  border: 1px solid rgba(155, 134, 201, 0.3);
  border-radius: 999px;
  padding: 6px 14px;
  font-family: var(--cr-utility);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  box-shadow: none;
  cursor: pointer; /* Pointer cursor for interactivity */
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

/* Button Hover Effects */
.reader-nav-button:hover {
  background: rgba(102, 51, 153, 0.5);
  border-color: rgba(240, 217, 163, 0.45);
  color: var(--cr-gold);
  box-shadow: none;
  transform: none;
}

/* Back Button Specific Styling */
.back-button {
  margin-top: 2px;
  margin-left: 12px; /* Push away from the edge */
}

/* Forward Button Specific Styling — the advancing action, tinted gold. */
.forward-button {
  color: var(--cr-gold);
  border-color: rgba(240, 217, 163, 0.28);
  margin-right: 12px; /* Push away from the edge */
}

/* Doorman v1 — Interview readiness emphasis.
   Applied to the Begin Reading button when the BE's Interview state
   signals ready_for_reading=true. The button is always rendered and
   clickable; this is purely a visual nudge that the LLM judges the
   seeker has shared enough context to begin. */
.forward-button.is-emphasized {
  background: linear-gradient(180deg, #7b4bb0, #4e2776);
  border-color: rgba(240, 217, 163, 0.55);
  color: var(--cr-gold);
  animation: doorman-nudge-pulse 2.6s ease-in-out infinite;
}

@keyframes doorman-nudge-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(240, 217, 163, 0.28); }
  50%      { box-shadow: 0 0 0 5px rgba(240, 217, 163, 0.02),
                         0 0 16px -2px rgba(240, 217, 163, 0.4); }
}
@media (prefers-reduced-motion: reduce) {
  .forward-button.is-emphasized { animation: none; }
}

/* Static suggestions — refined to match the reading-panel palette.
 * Was peach bubble + chunky purple borders + 24px bold; now a
 * transparent wrapper with refined lilac pills that read as a
 * matched pair instead of stoplight chips. */
.message-box.reader-response.static_suggestions {
  display: block;
  width: 100%;
  max-width: 100%;
  /* Sits close under the last card: the old 24px + 15px of air above and
     below pushed the advice card's caption off the top of the pane. */
  padding: 10px 0 6px;
  margin: 6px auto 4px;
  text-align: center;
  background: transparent;
  box-shadow: none;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  overflow: visible;
  box-sizing: border-box;
}

/* ── Start-menu quick-action cards ───────────────────────────── */
.qa-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(168px, 1fr));
  gap: 14px;
  width: 100%;
  max-width: 560px;
  /* Lowered slightly off the top; even 14px gap between the buttons + matching
     side padding so the spacing reads symmetric. */
  margin: 28px auto 14px;
  padding: 0 14px;
  box-sizing: border-box;
}
.qa-card {
  position: relative;
  height: 104px;
  display: block;
  padding: 0;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid rgba(255, 223, 163, 0.28);
  border-radius: 16px;
  overflow: hidden;
  background: #0a0015;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.5);
  transition: transform 0.35s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.35s, border-color 0.35s;
}
.qa-card:hover {
  transform: translateY(-4px);
  border-color: rgba(255, 223, 163, 0.6);
  box-shadow: 0 20px 46px rgba(0, 0, 0, 0.6), 0 0 28px rgba(255, 223, 163, 0.14);
}
.qa-card:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 3px; }

/* Default art = themed gradient + faded glyph (until a custom image is set) */
.qa-card__art {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.qa-card__art i {
  font-size: 40px;
  color: rgba(255, 255, 255, 0.10);
  transition: transform 0.4s, color 0.4s;
}
.qa-card:hover .qa-card__art i { transform: scale(1.08); color: rgba(255, 223, 163, 0.2); }
.qa-card.is-tarot .qa-card__art { background: radial-gradient(130% 100% at 50% 0%, rgba(126, 87, 194, 0.5), #0a0015 68%); }
.qa-card.is-practice .qa-card__art { background: radial-gradient(130% 100% at 50% 0%, rgba(201, 162, 39, 0.42), #0a0015 68%); }
.qa-card.is-astro .qa-card__art { background: radial-gradient(130% 100% at 50% 0%, rgba(76, 110, 194, 0.5), #0a0015 68%); }
.qa-card.is-generic .qa-card__art { background: radial-gradient(130% 100% at 50% 0%, rgba(149, 108, 255, 0.42), #0a0015 68%); }

.qa-card__title {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 34px 16px 16px;
  font-family: 'Cinzel', serif;
  font-size: 16px;
  font-weight: 500;
  color: #FFDFA3;
  line-height: 1.3;
  text-align: center; /* sit on the same vertical axis as the centered art glyph */
  background: linear-gradient(to top, rgba(7, 1, 14, 0.95) 0%, rgba(7, 1, 14, 0.5) 55%, transparent 100%);
}
.qa-card__price {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(10, 0, 21, 0.7);
  border: 1px solid rgba(125, 232, 204, 0.4);
  color: #7DE8CC;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(4px);
}
.qa-card__price .fa-gem { font-size: 11px; }
.qa-card__sheen {
  position: absolute;
  inset: 0;
  background: linear-gradient(115deg, transparent 35%, rgba(255, 223, 163, 0.1) 50%, transparent 65%);
  transform: translateX(-120%);
  transition: transform 0.7s ease;
  pointer-events: none;
}
.qa-card:hover .qa-card__sheen { transform: translateX(120%); }

.static_suggestions {
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  padding: 4px;
  box-sizing: border-box;
  flex-wrap: wrap;
}

.static_suggestions button {
  background: var(--cr-glass);  /* smoked glass */
  color: #e9dcff;
  border: 1px solid rgba(155, 134, 201, 0.34);
  border-radius: 999px;         /* pill */
  padding: 13px 26px;
  font-family: var(--cr-utility);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  text-align: center;
  min-width: 180px;
  box-shadow: none;
  transition: background 0.18s ease, border-color 0.18s ease,
              color 0.18s ease, transform 0.18s ease;
}

/* Compact row: branches of a topic, side by side; two per row on a phone. */
.static_suggestions.compact { gap: 8px; }
/* After the card drawn from a lesson (lesson-redo E2.6): three EQUAL buttons,
   one row when wide, full-width rows on a phone; only the gold fill marks the
   first, so "Change the subject" never wraps. */
.static_suggestions.lesson-row { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr); gap: 10px; }
.static_suggestions.lesson-row button, .static_suggestions.lesson-row button.qa-primary {
  min-width: 0; width: 100%; min-height: 52px; padding: 12px 14px; font-size: 13px; letter-spacing: 0.08em; white-space: nowrap;
}
@media (max-width: 699px) { .static_suggestions.lesson-row { grid-auto-flow: row; grid-auto-columns: auto; grid-template-columns: 1fr; } }
/* The lesson's card and its chips (2026-10-28): centred at one width. On a
   phone both are full width with a 16px gutter (the chat pads 12px each side). */
.chat-container { --tc-gutter-x: 0px; }
@media (max-width: 699px) { .chat-container { --tc-gutter-x: calc(2 * (16px - 11px)); } }
.message-box.reader-response.static_suggestions.lesson-chips {
  width: calc(100% - var(--tc-gutter-x, 0px)); max-width: 680px; margin: 2px auto 8px; padding: 0; align-self: center;
}
/* The card drawn from a lesson, on its own (2026-10-28): no outline box; the
   card and its one step sit centred in the room under the Mystic, and the
   box's edge comes back softly once the conversation opens (Go deeper). */
.chat-container { transition: border-color 600ms ease, background-color 600ms ease; }
.chat-container-panel { transition: background-color 600ms ease; }
.chat-container-panel.cr-lesson-stage-panel { background-color: transparent; padding-top: 0; }
/* No header pills here, so no scrim band over the room either, and no
   "In session with" tab: the Mystic is right there above the card. */
.chat-container-panel.cr-lesson-stage-panel .reader-nav-container { background: none; }
.chat-container-panel.cr-lesson-stage-panel .reader-name-tab { opacity: 0; visibility: hidden; }
.chat-container.cr-lesson-stage {
  border-color: transparent; background: transparent; justify-content: safe center; padding: 6px 12px 4px;
  /* Centred, so no room is kept under the card for pinning it to the top,
     and no row is kept over it for the session label (hidden here). */
  padding-bottom: 4px !important; margin-top: 0 !important;
  /* What is above the card (the opener) fades into the room, never a hard cut. */
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 18px);
  mask-image: linear-gradient(to bottom, transparent 0, #000 18px);
}
.chat-container.cr-lesson-stage .message-box.reader-response.static_suggestions.lesson-chips { margin-bottom: 2px; }
/* The line under the Mystic already says where it came from: the opener steps
   aside while the card is on its own, and is there again in the conversation. */
.message-box.cr-lesson-opener { max-height: 240px; transition: opacity 450ms ease, max-height 550ms ease, margin 550ms ease, padding 550ms ease; }
.chat-container.cr-lesson-stage .message-box.cr-lesson-opener { opacity: 0; max-height: 0; margin-top: 0; margin-bottom: 0; padding-top: 0; padding-bottom: 0; overflow: hidden; }
/* They wait, unseen but in place, while the card writes in; then fade up. */
.ldp-chips-held { visibility: hidden; opacity: 0; }
.ldp-chips-up { animation: ldp-chips-up 560ms cubic-bezier(.2, .7, .2, 1) both; }
@keyframes ldp-chips-up { from { opacity: 0; transform: translateY(10px); } }
/* "Get the full reading": the chips fade out before the hand-off line. */
.ldp-chips-leaving { opacity: 0; pointer-events: none; animation: ldp-chips-out 260ms ease both; }
@keyframes ldp-chips-out { from { opacity: 1; transform: none; } to { opacity: 0; transform: translateY(6px); } }
/* The lesson's opener fades in, the first thing the Mystic says. */
.message-box.cr-opener-in { animation: cr-opener-in 900ms ease-out both; }
@keyframes cr-opener-in { from { opacity: 0; transform: translateY(6px); } }
@media (prefers-reduced-motion: reduce) {
  .message-box.cr-opener-in, .ldp-chips-up { animation: none; }
  .ldp-chips-leaving { animation: none; }
}
/* A path offered in the sentence: the phrase itself is the control. */
.cr-choice-link { display: inline; padding: 0 1px; margin: 0; border: none; background: none; font: inherit; color: var(--cr-gold, #f0d9a3); text-decoration: underline; text-decoration-color: rgba(240,217,163,0.55); text-underline-offset: 3px; cursor: pointer; border-radius: 3px; }
.cr-choice-link:hover { text-decoration-color: var(--cr-gold, #f0d9a3); background: rgba(123,75,176,0.22); }
.cr-choice-link:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 2px; }
.static_suggestions.compact button { min-width: 0; padding: 10px 16px; font-size: 12px; letter-spacing: 0.06em; flex: 0 1 auto; }
/* Draw deeper's chips are her lines ("The Tower?", "What do I need to tell
   Sam?"): her own case, not the menu's uppercase. */
.static_suggestions button.qa-voice { text-transform: none; letter-spacing: 0.01em; font-size: 14px; }
@media (max-width: 699px) {
  /* Draw deeper's chips follow her reading instead of pinning over it: four
     lines pinned to a short phone chat covered the card they answer. */
  .chat-container > .message-box.static_suggestions.deeper-flow { position: static; background: none; }
  /* Her four lines stay two short rows. */
  .static_suggestions.compact button.qa-voice { font-size: 13px; padding: 9px 12px; min-height: 44px; flex: 1 1 calc(50% - 8px); }
}
.static_suggestions button:hover {
  background: rgba(102, 51, 153, 0.55);
  border-color: rgba(240, 217, 163, 0.5);
  color: var(--cr-gold);
  transform: translateY(-1px);
}
/* Rate / Share after a landed reading: the two asks the reading makes of the
   seeker. Filled purple with the gold ink of the reading's own controls, and
   larger than a chip, so they read as the actions rather than afterthoughts. */
.static_suggestions button.qa-primary {
  background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #FFDFA3;
  border: 1px solid rgba(240, 217, 163, 0.55);
  padding: 15px 30px;
  font-size: 14px;
  min-width: 210px;
  box-shadow: 0 1px 0 rgba(255,255,255,0.12) inset, 0 12px 30px -8px rgba(78, 39, 118, 0.6);
}
.static_suggestions button.qa-primary:hover {
  background: linear-gradient(180deg, #8a5bc4, #5a2f87);
  border-color: rgba(240, 217, 163, 0.8);
  color: #FFDFA3;
}

/* A landed rating (3+): the bar's SHARE gets one soft gold glow, once. */
.static_suggestions button.qa-landed {
  animation: share-landed-glow 1.8s ease-out 1 both;
}
@keyframes share-landed-glow {
  0%   { box-shadow: 0 0 0 0 rgba(231, 201, 131, 0); }
  35%  { box-shadow: 0 0 0 7px rgba(231, 201, 131, .38), 0 0 22px 4px rgba(231, 201, 131, .35); }
  100% { box-shadow: 0 0 0 0 rgba(231, 201, 131, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .static_suggestions button.qa-landed { animation: none; border-color: #f0d9a3; box-shadow: 0 0 0 2px rgba(231, 201, 131, .55); }
}
.qa-primary-glyph { color: #f0d9a3; margin-right: 9px; font-size: 0.95em; }
/* A gift that went out (DESIGN 2026-10-22 §4, A1): one end block. The rating
   (while unrated), then Finish reading in gold and the gift beside it. */
.chat-container .message-box.static_suggestions.gift-end, .message-box.static_suggestions.gift-end {
  width: min(560px, calc(100% - 24px)); max-width: 560px; margin: 10px auto 8px; align-self: center; padding: 14px 16px 18px; box-sizing: border-box; border-radius: 22px;
  background: radial-gradient(125% 95% at 50% 0%, #1c1035 0%, #120827 54%, #0d0520 100%);
  border: 1px solid rgba(231, 201, 131, .2);
}
.gift-end :deep(.cer) { margin: 0 auto 6px; background: none; border: 0; padding: 6px 0 4px; }
.static_suggestions.gift-end-row { justify-content: center; gap: 12px; }
.static_suggestions button.qa-finish {
  background: linear-gradient(180deg, #f3dca0, #d9b768); color: #2a1845; border: 1px solid rgba(255, 236, 190, .8);
  min-width: 190px; min-height: 48px; box-shadow: 0 10px 26px -10px rgba(231, 201, 131, .55);
}
.static_suggestions button.qa-finish:hover { background: linear-gradient(180deg, #f7e4b2, #e0c07a); color: #2a1845; border-color: #fff0c8; }
.static_suggestions button.qa-gift-open { min-width: 190px; min-height: 48px; background: rgba(13, 5, 32, .6); color: #f4ecff;
  border: 1px solid rgba(155, 134, 201, .45); }
.static_suggestions button.qa-gift-open .qa-primary-glyph { color: #f0d9a3; }
@media (max-width: 560px) {
  .static_suggestions.gift-end-row { flex-direction: column; align-items: stretch; }
  .static_suggestions.gift-end-row button { width: 100%; min-width: 0; }
}
/* Logged-out viewer's Login CTA — filled brand purple so it reads as the
   primary action standing in for Rate/Share. */
.static_suggestions button.qa-login-cta {
  background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #fbf7f0;
  border-color: #4e2776;
}
/* "Reveal Myself (Login)" — the primary post-reading CTA. Elevate it from the
   flat lilac pill to a filled brand-purple + gold treatment (matching the
   reading's Reveal button) so it reads as THE action, gently pulsing for pull. */
.static_suggestions button.qa-reveal-cta {
  background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #FFDFA3;
  border: 1px solid rgba(240, 217, 163, 0.55);
  box-shadow: 0 1px 0 rgba(255,255,255,0.12) inset, 0 12px 30px -8px rgba(78, 39, 118, 0.6);
  animation: qa-reveal-pulse 2.6s ease-in-out infinite;
}
.static_suggestions button.qa-reveal-cta:hover {
  background: linear-gradient(180deg, #8a5bc4, #5a2f87);
  border-color: rgba(240, 217, 163, 0.8);
  color: #FFDFA3;
  transform: translateY(-1px);
  animation: none;
}
.qa-reveal-glyph { color: #f0d9a3; margin-right: 9px; font-size: 0.95em; }
@keyframes qa-reveal-pulse {
  0%, 100% { box-shadow: 0 1px 0 rgba(255,255,255,0.12) inset, 0 12px 30px -8px rgba(78, 39, 118, 0.55); }
  50% { box-shadow: 0 1px 0 rgba(255,255,255,0.12) inset, 0 12px 30px -6px rgba(240, 217, 163, 0.45); }
}
@media (prefers-reduced-motion: reduce) {
  .static_suggestions button.qa-reveal-cta { animation: none; }
}
.loading-container {
  display: flex;
  /* .message-box sets column; the spinner belongs beside the words. */
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 10px;
  font-family: var(--cr-serif);
  font-size: 16px;
  font-style: italic;
  color: var(--cr-lavender);
}
/* Grounding-beat countdown ring — a slow gold circle that depletes over the
   ~10s grounding pause. Duration MUST match salutations.py
   GROUNDING_PAUSE_SECONDS so it empties just as the funnel arrives. */
.loading-container.is-grounding-countdown {
  padding: 18px 10px 22px;
}
/* THE GROUNDING PAUSE
   Ten seconds the backend holds on purpose. It was rendering as a line of chat
   at the top of a transcript pane with dead space beneath — the shape of a
   conversation waiting to continue, when what it is is a threshold. The pane
   composes around it instead: the beat centred in the space, everything else
   out of the way, and a slow breath of light to rest on. */
.chat-container.cr-grounding {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  position: relative;
  gap: 0.25rem;
  /* The glow is a 46rem circle hung off this box; unclipped, it grew the
     scroll height and the pane's bottom-pin scrolled the beat itself out of
     view — a blank pane for the whole held pause. */
  overflow: hidden;
}
/* Breath-paced, not attention-seeking: ~9s is a long slow inhale and release,
   close to the pace you would coach someone to settle at. */
.cr-grounding-glow {
  position: absolute;
  left: 50%;
  top: 46%;
  width: min(46rem, 116%);
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  pointer-events: none;
  border-radius: 50%;
  background: radial-gradient(circle,
    rgba(195, 169, 255, 0.15) 0%,
    rgba(120, 86, 190, 0.09) 34%,
    rgba(0, 0, 0, 0) 68%);
  animation: cr-grounding-breath 9s ease-in-out infinite;
}
@keyframes cr-grounding-breath {
  0%, 100% { opacity: 0.55; transform: translate(-50%, -50%) scale(0.94); }
  50%      { opacity: 1;    transform: translate(-50%, -50%) scale(1.06); }
}
/* Everything in the pane sits above the light. */
.chat-container.cr-grounding > *:not(.cr-grounding-glow) { position: relative; z-index: 1; }
/* The countdown ring is the one companion the beat keeps. Give it room, and
   make it a slow meter rather than a spinner: wider, thinner, dimmer. At
   56px/3px beside 1.7rem serif it read as "loading", which is the opposite of
   the instruction to wait on purpose. */
.chat-container.cr-grounding .loading-container {
  margin-top: 2.25rem; background: none; border: none; box-shadow: none;
}
.chat-container.cr-grounding .gc-ring { width: 76px; height: 76px; opacity: 0.72; }
.chat-container.cr-grounding .gc-track { stroke-width: 1.25; }
.chat-container.cr-grounding .gc-progress { stroke-width: 1.25; }

/* The grounding beat is the ONLY thing on screen while the backend holds the
   pause, and it rendered as an ordinary bubble — same size, same left-aligned
   paragraph as any passing remark. A sentence asking someone to settle should
   not look like a line of chat in an empty room. Centred, larger, and with the
   bubble chrome removed, since there is nothing here to contain. */
.message-box.is-grounding-beat {
  background: none;
  border: none;
  box-shadow: none;
  max-width: 34rem;
  margin: 2.25rem auto 1.5rem;
  padding: 0 1.25rem;
  text-align: center;
  /* The serif the readings themselves are set in — this is the Mystic
     speaking, not interface chrome. */
  font-family: var(--cr-serif);
  font-size: 1.72rem;
  line-height: 1.55;
  letter-spacing: 0.008em;
  color: #f6efe0;
  text-shadow: 0 1px 24px rgba(120, 86, 190, 0.45);
  animation: grounding-beat-in 1400ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
}
.message-box.was-grounding-beat {
  /* Settled: the Mystic's first words stay, as a line of the conversation. */
  font-family: var(--cr-serif);
  color: #f6efe0;
  transition: font-size 600ms ease, margin 600ms ease, padding 600ms ease;
}
.message-box.is-grounding-beat .text-message {
  font-size: inherit;
  line-height: inherit;
  text-align: inherit;
}
/* Arrives rather than appears — slow enough to read as settling. */
@keyframes grounding-beat-in {
  from { opacity: 0; transform: translateY(10px); filter: blur(3px); }
  to   { opacity: 1; transform: none; filter: none; }
}
/* Arriving is the point; the animation is not. */
@media (prefers-reduced-motion: reduce) {
  .message-box.is-grounding-beat { animation: none; }
  .cr-grounding-glow { animation: none; opacity: 0.75; }
}
@media (max-width: 700px) {
  .message-box.is-grounding-beat { font-size: 1.32rem; margin-top: 1rem; }
  .cr-grounding-glow { width: min(28rem, 130%); }
}

/* Before the line: the dots sit where it will appear. They fade out as the
   line fades in, taken out of the flow so the line does not jump. */
.grounding-arriving {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 4.5rem;
  margin: 2.25rem auto 1.5rem;
  font-size: 1rem;
}
.gd-fade-enter-active { transition: opacity 500ms ease; }
.gd-fade-leave-active {
  transition: opacity 900ms ease;
  position: absolute !important;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}
.gd-fade-enter-from, .gd-fade-leave-to { opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .gd-fade-enter-active, .gd-fade-leave-active { transition: none; }
}
/* Phones: the pane between the portrait and the composer is short. The line
   and the ring must both sit inside it for the whole held pause; with the
   desktop margins the ring hung under the composer, only its top arc showing
   ("still no loading icon countdown?", Chris 2026-10-19). */
@media (max-width: 700px) {
  /* The pane runs on under the composer here, so centring in it put the ring
     below the visible part. Sit the beat at the top of what can be seen. */
  .chat-container.cr-grounding { justify-content: flex-start; }
  .message-box.is-grounding-beat { margin: 0.5rem auto 0.25rem; }
  .chat-container.cr-grounding .loading-container { margin-top: 0.25rem; }
  .loading-container.is-grounding-countdown { padding: 6px 10px 8px; }
  .chat-container.cr-grounding .gc-ring { width: 52px; height: 52px; }
  .grounding-arriving { margin: 0.5rem auto; }
  /* The grounding plays in the Doorman state (cr-menu), whose greeting rule
     (.chat-container.cr-menu .message-box.reader-response) outranked the two
     lines above: the beat and the ring's box kept the menu's margins and the
     ring slid under the Respond box (iPhone 13, e2e 2026-10-31). */
  .chat-container.cr-grounding .message-box.reader-response.is-grounding-beat { margin: 0.5rem auto 0.25rem; }
  .chat-container.cr-grounding .message-box.reader-response.loading-container { margin: 0.25rem auto 0; padding: 6px 10px 8px; }
}

.grounding-countdown {
  display: flex;
  align-items: center;
  justify-content: center;
}
.gc-ring {
  width: 56px;
  height: 56px;
  transform: rotate(-90deg); /* start the sweep from 12 o'clock */
}
.gc-track {
  fill: none;
  stroke: rgba(240, 217, 163, 0.14);
  stroke-width: 3;
}
.gc-progress {
  fill: none;
  stroke: #e9cf86;
  stroke-width: 3;
  stroke-linecap: round;
  /* r=19 → circumference = 2·π·19 ≈ 119.38 */
  stroke-dasharray: 119.38;
  stroke-dashoffset: 0;
  filter: drop-shadow(0 0 5px rgba(233, 207, 134, 0.5));
  animation: gc-deplete 10s linear forwards;
}
@keyframes gc-deplete {
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: 119.38; }
}
@media (prefers-reduced-motion: reduce) {
  .gc-progress { animation: none; stroke-dashoffset: 59.69; }
}
    @media (max-width: 768px) {
      /* 481-768px used to get 24px chips (6px padding, 11px type): the worst
         targets in the flow, in the band a small tablet or a phone in
         landscape lands in. */
      .static_suggestions button {
        padding: 12px 14px;
        font-size: 13px;
        min-height: 44px;
      }
    }
    @media (max-width: 480px) {
      .chat-container {
        flex: 1 1 auto !important;
        height: 100%           !important;
        overflow-y: auto       !important;
      }
      /* Center the chat window on phones. `scrollbar-gutter: stable both-edges`
         is meant to inset both sides equally, but Safari/iOS insets only the
         RIGHT edge, so the whole white chat pane drifts left of the centered
         avatar + Menu bar. iOS uses overlay scrollbars (no layout width), so
         dropping the reserved gutter here centers the pane without any scroll
         regression. */
      .chat-container,
      .chat-box-container {
        scrollbar-gutter: auto;
      }
      .chat-container-panel {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto !important;
        height: 100%;

        overflow-y: visible !important;
        box-sizing: border-box;

      }
      .chat-container-panel.no-menu .follow-up-container {
        bottom: env(safe-area-inset-bottom, 0) !important;
      }
      .chat-box-container {
    flex: 1 1 auto !important;
    overflow-y: auto !important;
      }
      /* The header row is display:none here, so the scroller reclaims
         the headroom it was reserving for it. */
      .chat-container { padding: 12px 10px; }
      .cr-preparing { margin: -12px -10px; }
      .message-box {
        max-width: 92%;
        margin: 15px 12px 15px 0;
        padding: 0;
        box-shadow: none;
      }
      .reader-response { max-width: 94%; padding-left: 15px; }
      .user-message {
        margin: 15px 0 15px 12px;
        max-width: 84%;
        padding: 10px 15px;
        border-radius: 18px 18px 6px 18px; /* .message-box above would flatten it */
      }
      .message-box p { font-size: 17px; }
      .user-message p { font-size: 16px; }
      .card-message {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: linear-gradient(180deg, rgba(38, 27, 66, 0.9), rgba(14, 6, 34, 0.94));
        border: 1px solid rgba(240, 217, 163, 0.22);
        border-radius: 14px;
        padding: 12px;
        width: 100%;
        gap: 10px;
        box-shadow: 0 14px 30px -18px rgba(0, 0, 0, 0.95);
      }
      .card-title {
        font-size: 10px;
        letter-spacing: 0.12em;
        color: var(--cr-lavender);
        padding: 0;
        background: none;
        border: none;
        box-shadow: none;
        text-align: center;
        word-wrap: break-word;
        width: 100%; /* Allow width to adjust to content */
        margin: 0;
      }
      .card-content {
        display: flex;
        flex-direction: row; /* Place title and image side by side */
        align-items: center;
        justify-content: flex-start;
        width: 100%;
      }
      .card-text-container {
        text-align: center;
        width: 100%;
      }
      .card-caption {
        font-size: 1.05em;
        color: var(--cr-gold);
        text-align: center;
        background: none;
        padding: 4px 2px;
        border: none;
        box-shadow: none;
        width: 100%;
        margin: 0;
      }
      .card-image {
        width: 92px;
        height: auto;
        max-height: 150px;
        margin: 0;
        border-radius: 8px;
        border: 1px solid rgba(240, 217, 163, 0.3);
        object-fit: cover;
        box-shadow: 0 8px 18px -8px rgba(0, 0, 0, 0.85);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
      }
      .follow-up-container {
        position: sticky;
        bottom: 0;
        z-index: 10;
        flex-shrink: 0;
        background: var(--cr-ground);
      }

      .follow-up-container textarea {
        flex: 1;
        font-size: 16px;
        padding: 8px 15px;
        border-radius: 20px;
      }

      .follow-up-container > button {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        font-size: 13px;
        padding: 0;
        border-radius: 50%;
      }

      /* Move the reading actions (Exit / Subject / Cards) out of the cramped
         top overlay to a bar just above the chat input. They're DOM-ordered
         between the messages and the input, so dropping the absolute positioning
         lets them flow to exactly the right spot. */
      /* The reading actions now live in the gear menu to the left of the input
         (saves the whole button row's vertical space). Hide the row on phones. */
      .reader-nav-container { display: none; }

      /* Gear trigger + its upward dropdown menu of reading actions. A clean
         ghost circle so it reads as a light secondary next to the filled send. */
      .reading-actions-menu button {
        width: 40px;
        height: 40px;
        padding: 0;
        border-radius: 50%;
        background: transparent;
        border: 1px solid rgba(155, 134, 201, 0.32);
        color: var(--cr-lavender);
        font-size: 16px;
        transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
      }
      .reading-actions-menu button:hover {
        background: rgba(102, 51, 153, 0.35);
        border-color: rgba(240, 217, 163, 0.45);
        color: var(--cr-gold);
      }
      .reading-actions-menu button:active { transform: scale(0.92); }
      .reading-actions-menu button.loading-spinner {
        border-color: rgba(155, 134, 201, 0.22);
        color: rgba(155, 134, 201, 0.7);
      }
      .quick-actions-dropdown {
        bottom: calc(100% + 8px);
        left: 6px;
        right: auto;
        min-width: 190px;
        max-width: calc(100vw - 24px);
        background: #1c1030;
        border: 1px solid rgba(195, 169, 255, 0.35);
        border-radius: 12px;
        padding: 6px;
        box-shadow: 0 12px 32px -8px rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        gap: 2px;
        z-index: 60;
      }
      .quick-actions-dropdown button {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 11px;
        text-align: left;
        width: 100%;
        padding: 11px 14px;
        border-radius: 8px;
        background: transparent;
        color: #ece7f6;
        font-size: 14px;
        white-space: nowrap;
      }
      .quick-actions-dropdown .qa-action-icon {
        flex: none;
        width: 16px;
        text-align: center;
        font-size: 13px;
        color: #c3a9ff;
      }
      .quick-actions-dropdown button:hover .qa-action-icon,
      .quick-actions-dropdown button:active .qa-action-icon { color: #f0d9a3; }
      .quick-actions-dropdown button:hover,
      .quick-actions-dropdown button:active {
        background: rgba(102, 51, 153, 0.35);
      }
      .reader-nav-button {
        font-size: 14px;
        padding: 4px 8px;
        border-radius: 8px;
      }
      .quick-actions-trigger button {
        font-size: 14px;
        min-width: 44px;
        min-height: 44px;
        border-radius: 10px;
      }
      /* (A second, stale .quick-actions-dropdown block used to live here and
         won by source order: 21px rows. Deleted 2026-09-24.) */
      .static_suggestions {
        flex-direction: column;
        width: 100%;
        padding: 0;
        gap: 5px;
      }
      .static_suggestions button {
        font-size: 12px;
        padding: 11px 16px;
        min-width: 0;
        border-radius: 999px;
      }
      /* Branches of a topic (three or more chips): two per row on a phone,
         labels allowed to wrap, so the fork reads as a fork, not a list. */
      .static_suggestions.compact { flex-direction: row; flex-wrap: wrap; gap: 8px; }
      .static_suggestions.compact button { flex: 1 1 calc(50% - 8px); font-size: 13px; padding: 12px 12px; min-height: 48px; white-space: normal; line-height: 1.25; }
    }
.message-box.reader-response.birthplace-map-wrapper {
  max-width: 100% !important;
  width: 100% !important;
  padding: 0 !important;
  margin: 0 auto !important;
  background-color: transparent !important;
  box-shadow: none !important;
}



/* Chips are tools (Chris, 2026-10-27): Draw the cards, Talk it through,
   Share, Finish, Ask my own question. They do something TO the session, so
   they look like controls, not lines of dialogue: plain, small, sentence case,
   one quiet row under the conversation. What she might SAY is a link in the
   Mystic's line instead (.cr-choice-link). The visible chip is 32px; its
   ::after reaches a 44px tap target. Last in the sheet, so it wins over the
   older pill rules above (qa-primary, qa-finish, the phone column). */
.message-box .static_suggestions.tool-row,
.message-box.static_suggestions .static_suggestions.tool-row {
  flex-direction: row; flex-wrap: wrap; justify-content: center; align-items: center;
  gap: 10px 8px; padding: 6px 4px;
}
.message-box .static_suggestions.tool-row > button {
  position: relative; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  flex: 0 0 auto; min-width: 0; min-height: 32px; width: auto; margin: 0; padding: 5px 12px;
  border-radius: 8px; border: 1px solid rgba(233, 220, 255, 0.2); background: rgba(255, 255, 255, 0.04);
  color: #ddd2f0; box-shadow: none; animation: none;
  font-family: var(--cr-utility); font-size: 12.5px; font-weight: 500; line-height: 1.2;
  letter-spacing: 0.01em; text-transform: none; white-space: nowrap;
}
.message-box .static_suggestions.tool-row > button::after { content: ''; position: absolute; inset: -6px -2px; }
.message-box .static_suggestions.tool-row > button:hover {
  background: rgba(255, 255, 255, 0.08); border-color: rgba(240, 217, 163, 0.45); color: #f0d9a3; transform: none;
}
.message-box .static_suggestions.tool-row > button:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 2px; }
/* The tool the moment is for (Rate / Share, Finish after a gift, sign in,
   Draw the cards): the same chip, gold ink, no fill. */
.message-box .static_suggestions.tool-row > button.qa-primary,
.message-box .static_suggestions.tool-row > button.qa-finish,
.message-box .static_suggestions.tool-row > button.qa-reveal-cta,
.message-box .static_suggestions.tool-row > button.qa-login-cta,
.message-box .static_suggestions.tool-row > button.qa-gift-open,
.message-box .static_suggestions.tool-row > button[data-testid="draw-the-cards"] {
  background: rgba(240, 217, 163, 0.06); border-color: rgba(240, 217, 163, 0.42); color: #f0d9a3;
  min-width: 0; min-height: 32px; padding: 5px 12px; font-size: 12.5px; box-shadow: none;
}
.message-box .static_suggestions.tool-row > button .qa-primary-glyph,
.message-box .static_suggestions.tool-row > button .qa-reveal-glyph { margin-right: 0; font-size: 11px; color: inherit; }
/* After the card drawn from a lesson (lesson-redo E2.6, walk B3): three
   EQUAL buttons, one row when wide and full-width rows on a phone; only the
   gold fill marks the first. After the tool-row rules, so they hold. */
.message-box .static_suggestions.tool-row.lesson-row {
  display: grid; grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr); gap: 10px; padding: 6px 4px;
}
.message-box .static_suggestions.tool-row.lesson-row > button {
  width: 100%; min-width: 0; min-height: 44px; padding: 10px 14px; font-size: 13.5px; font-weight: 600;
  letter-spacing: 0.02em; white-space: nowrap; border-radius: 10px;
}
.message-box .static_suggestions.tool-row.lesson-row > button.qa-primary {
  background: linear-gradient(180deg, #f3dca4, #d9b46a); border-color: #e7c983; color: #2a1640;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.35) inset, 0 8px 22px -10px rgba(231, 201, 131, 0.7);
}
.message-box .static_suggestions.tool-row.lesson-row > button.qa-primary:hover {
  background: linear-gradient(180deg, #f7e4b4, #e0bd77); color: #2a1640;
}
/* A row led by Draw the cards (2026-10-29): the primary first, in the same
   gold fill as Go deeper, with dark ink, a soft glow and a small lift; the
   rest are quiet ghosts at the same height. */
.message-box .static_suggestions.tool-row > button.qa-primary-fill,
.message-box .static_suggestions.tool-row > button.qa-ghost {
  min-height: 40px; padding: 9px 18px; border-radius: 10px; font-size: 13.5px; letter-spacing: 0.02em;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}
.message-box .static_suggestions.tool-row > button.qa-primary-fill {
  background: linear-gradient(180deg, #f3dca4, #d9b46a); border-color: #e7c983; color: #2a1640; font-weight: 600;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.35) inset, 0 8px 22px -10px rgba(231, 201, 131, 0.7);
}
.message-box .static_suggestions.tool-row > button.qa-primary-fill:hover {
  background: linear-gradient(180deg, #f7e4b4, #e0bd77); border-color: #f0d9a3; color: #2a1640; transform: translateY(-1px);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.4) inset, 0 12px 26px -10px rgba(231, 201, 131, 0.85);
}
.message-box .static_suggestions.tool-row > button.qa-primary-fill:active { transform: none; }
.message-box .static_suggestions.tool-row > button.qa-primary-fill:focus-visible {
  outline: 2px solid #FFDFA3; outline-offset: 3px;
}
.message-box .static_suggestions.tool-row > button.qa-primary-fill .qa-primary-glyph { font-size: 12px; color: inherit; }
.message-box .static_suggestions.tool-row > button.qa-ghost {
  background: transparent; border-color: rgba(233, 220, 255, 0.22); color: #b9abd3; font-weight: 500;
}
.message-box .static_suggestions.tool-row > button.qa-ghost:hover {
  background: rgba(255, 255, 255, 0.04); border-color: rgba(240, 217, 163, 0.45); color: #f0d9a3;
}
.message-box .static_suggestions.tool-row > button.qa-ghost:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 3px; }
@media (prefers-reduced-motion: reduce) {
  .message-box .static_suggestions.tool-row > button.qa-primary-fill:hover { transform: none; }
}
.lesson-carat { font-size: 0.95em; line-height: 1; }
/* The one step after a lesson's card (2026-10-28): ✦ Go deeper with {Mystic} · 50 ✦. */
.lesson-orn { font-size: 0.95em; line-height: 1; opacity: 0.75; }
.lesson-chip-t { margin: 0 0.2em; }
/* Under the lesson's card the row's edges are the card's (no inset). */
.message-box.lesson-chips .static_suggestions.tool-row.lesson-row { padding: 6px 0; }
@media (max-width: 699px) {
  .message-box .static_suggestions.tool-row.lesson-row { grid-auto-flow: row; grid-auto-columns: auto; grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .message-box .static_suggestions.tool-row > button { min-height: 34px; font-size: 13px; padding: 6px 12px; }
  .message-box .static_suggestions.tool-row > button::after { inset: -5px -2px; }
  .message-box .static_suggestions.gift-end-row.tool-row { flex-direction: row; align-items: center; }
  .message-box .static_suggestions.gift-end-row.tool-row > button { width: auto; }
}
/* Quiet (gift room walk #6, shot 25): small, italic, muted. */
.message-box.reader-response .text-message.cr-else { margin-top: 10px; font-family: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  font-style: italic; font-size: 13.5px; line-height: 1.4; color: #9b86c9; }
/* The session label's own row on a phone (gift room walk #5). */
.cr-session-label { display: none; }
@media (max-width: 480px) {
  .cr-session-label { display: block; flex: none; padding: 6px 12px 4px; text-align: center; color: var(--cr-lavender);
    font-family: var(--cr-utility); font-size: 10.5px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
}
</style>

